import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Canvas, useFrame, useThree, type ThreeEvent } from "@react-three/fiber";
import { Edges, Grid as DreiGrid, OrbitControls, Text } from "@react-three/drei";
import * as THREE from "three";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import ThemeToggle from "./ThemeToggle";
import { useTheme } from "../lib/theme";
import { MoreVertical } from "lucide-react";
import { MarkPad, evaluateMarkLines, type MarkEvaluation, type MarkObject, type MarkLabel } from "./MarkPad";
// Using a generic type for the OrbitControls ref to avoid needing three-stdlib types
type OrbitControlsImpl = any;

function UpdateCameraFar({ far }: { far: number }) {
  const { camera } = useThree();
  useEffect(() => {
    // Narrow to PerspectiveCamera before accessing .far
    const persp = camera as THREE.PerspectiveCamera;
    if (typeof (persp as any).far === "number" && persp.far !== far) {
      persp.far = far;
      persp.updateProjectionMatrix();
    }
  }, [camera, far]);
  return null;
}

type Unit = "m" | "ft";

type BoxItem = {
  id: number;
  position: [number, number, number];
  size: [number, number, number];
  color: string;
  groupId: number;
};

type SceneData = {
  id: string;
  name: string;
  spacingValue: number;
  unit: Unit;
  boxes: BoxItem[];
  nextId: number;
  nextGroupId: number;
  updatedAt: number;
};

type SceneStore = {
  scenes: SceneData[];
  activeId: string;
};

const FT_TO_M = 0.3048;

function toMeters(value: number, unit: Unit) {
  return unit === "ft" ? value * FT_TO_M : value;
}

function fromMeters(value: number, unit: Unit) {
  return unit === "ft" ? value / FT_TO_M : value;
}

function fromMetersSquared(value: number, unit: Unit) {
  const conversion = FT_TO_M * FT_TO_M;
  return unit === "ft" ? value / conversion : value;
}

function fromMetersCubed(value: number, unit: Unit) {
  const conversion = FT_TO_M * FT_TO_M * FT_TO_M;
  return unit === "ft" ? value / conversion : value;
}

const VOLUME_UNIT_LABEL: Record<Unit, string> = {
  m: "m³",
  ft: "ft³",
};

const AREA_UNIT_LABEL: Record<Unit, string> = {
  m: "m²",
  ft: "ft²",
};

function roundTo(value: number, decimals = 4) {
  const factor = Math.pow(10, decimals);
  return Math.round(value * factor) / factor;
}

function formatMetricValue(value: number) {
  const abs = Math.abs(value);
  let maximumFractionDigits = 3;
  if (abs >= 100) maximumFractionDigits = 0;
  else if (abs >= 10) maximumFractionDigits = 1;
  else if (abs >= 1) maximumFractionDigits = 2;
  return value.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits,
  });
}

function formatMetricWithUnit(value: number, unitLabel: string) {
  return `${formatMetricValue(value)} ${unitLabel}`;
}

const UNIT_CONFIG: Record<Unit, { min: number; step: number; precision: number }> = {
  m: { min: 0.05, step: 0.05, precision: 3 },
  ft: { min: 0.25, step: 0.25, precision: 2 },
};

function normalizeSpacingValue(value: number, unit: Unit) {
  const cfg = UNIT_CONFIG[unit];
  if (!Number.isFinite(value)) return cfg.min;
  const clamped = Math.max(cfg.min, value);
  const snapped = Math.max(cfg.min, Math.round(clamped / cfg.step) * cfg.step);
  return roundTo(snapped, cfg.precision);
}

const SCENE_STORAGE_KEY = "space-calc/scenes/v1";
const SCENE_ACTION_CREATE = "__scene_create__";
const WORKSPACE_STORAGE_KEY = "space-calc/workspace-settings/v1";
const DEFAULT_MARK_LINES = [
  "m = 1, ft = 0.3048 m",
  "",
  "// Room dimensions",
  "width = 4 m",
  "depth = 3 m", 
  "height = 2.5 m",
  "",
  "// Main structure",
  "box(width, depth, height, color: sky) #vis",
  "area = width * depth #vis",
  "volume = width * depth * height #vis",
  "",
  "// Furniture",
  "box(1.5, 1, 0.8, color: '#8b5cf6') #vis",
  "Desk #vis",
];

type WorkspaceSettings = {
  markModeEnabled: boolean;
  markLines: string[];
};

function createDefaultWorkspaceSettings(): WorkspaceSettings {
  return {
    markModeEnabled: false,
    markLines: DEFAULT_MARK_LINES,
  };
}

function loadWorkspaceSettings(): WorkspaceSettings {
  if (typeof window === "undefined") return createDefaultWorkspaceSettings();
  try {
    const raw = window.localStorage.getItem(WORKSPACE_STORAGE_KEY);
    if (!raw) return createDefaultWorkspaceSettings();
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return createDefaultWorkspaceSettings();
    const markModeEnabled = Boolean(parsed.markModeEnabled);
    const markLines = Array.isArray(parsed.markLines) ? parsed.markLines.map((l: any) => String(l)) : DEFAULT_MARK_LINES;
    return {
      markModeEnabled,
      markLines: markLines.length ? markLines : DEFAULT_MARK_LINES,
    };
  } catch (error) {
    console.warn("Failed to load workspace settings", error);
    return createDefaultWorkspaceSettings();
  }
}

function persistWorkspaceSettings(settings: WorkspaceSettings) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(WORKSPACE_STORAGE_KEY, JSON.stringify(settings));
  } catch (error) {
    console.warn("Failed to persist workspace settings", error);
  }
}

function cloneTuple(tuple: [number, number, number]): [number, number, number] {
  return [tuple[0], tuple[1], tuple[2]];
}

function cloneBoxes(items: BoxItem[]): BoxItem[] {
  return items.map(item => ({
    ...item,
    position: cloneTuple(item.position),
    size: cloneTuple(item.size),
  }));
}

function areBoxesEqual(a: BoxItem[], b: BoxItem[]): boolean {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  const lookup = new Map<number, BoxItem>();
  for (const box of a) {
    lookup.set(box.id, box);
  }
  for (const other of b) {
    const match = lookup.get(other.id);
    if (!match) return false;
    if (match.color !== other.color || match.groupId !== other.groupId) return false;
    if (
      match.position[0] !== other.position[0]
      || match.position[1] !== other.position[1]
      || match.position[2] !== other.position[2]
    ) {
      return false;
    }
    if (
      match.size[0] !== other.size[0]
      || match.size[1] !== other.size[1]
      || match.size[2] !== other.size[2]
    ) {
      return false;
    }
  }
  return true;
}

function makeSceneId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `scene-${Date.now()}-${Math.floor(Math.random() * 100000)}`;
}

function generateSceneName(existing: SceneData[]): string {
  const usedNumbers = new Set<number>();
  for (const scene of existing) {
    const match = /^Scene\s+(\d+)$/.exec(scene.name);
    if (match) usedNumbers.add(Number(match[1]));
  }
  for (let i = 1; i <= existing.length + 5; i++) {
    if (!usedNumbers.has(i)) return `Scene ${i}`;
  }
  return `Scene ${existing.length + 1}`;
}

function createSceneData(partial?: Partial<SceneData>): SceneData {
  const unit = partial?.unit ?? "m";
  const spacingCandidate = partial?.spacingValue;
  const spacingValue = Number.isFinite(spacingCandidate)
    ? normalizeSpacingValue(spacingCandidate as number, unit)
    : normalizeSpacingValue(1, unit);
  return {
    id: partial?.id ?? makeSceneId(),
    name: partial?.name ?? "Scene",
    spacingValue,
    unit,
    boxes: partial?.boxes ? cloneBoxes(partial.boxes) : [],
    nextId: partial?.nextId ?? 1,
    nextGroupId: partial?.nextGroupId ?? 1,
    updatedAt: partial?.updatedAt ?? Date.now(),
  };
}

function sanitizeSceneData(raw: any, fallbackName: string): SceneData | null {
  if (!raw || typeof raw !== "object") return null;
  const unit: Unit = raw.unit === "ft" ? "ft" : "m";
  const boxesInput = Array.isArray(raw.boxes) ? raw.boxes : [];
  const boxes: BoxItem[] = [];
  for (const candidate of boxesInput) {
    if (!candidate || typeof candidate !== "object") continue;
    const posArray = Array.isArray(candidate.position) ? candidate.position : [];
    const sizeArray = Array.isArray(candidate.size) ? candidate.size : [];
    if (posArray.length !== 3 || sizeArray.length !== 3) continue;
    const position: [number, number, number] = [
      Number(posArray[0]) || 0,
      Number(posArray[1]) || 0,
      Number(posArray[2]) || 0,
    ];
    const size: [number, number, number] = [
      Math.max(0, Number(sizeArray[0]) || 0),
      Math.max(0, Number(sizeArray[1]) || 0),
      Math.max(0, Number(sizeArray[2]) || 0),
    ];
    const id = Number(candidate.id);
    const groupId = Number(candidate.groupId);
    const color = typeof candidate.color === "string" ? candidate.color : "#999999";
    boxes.push({
      id: Number.isFinite(id) ? id : boxes.length + 1,
      position,
      size,
      color,
      groupId: Number.isFinite(groupId) ? groupId : 1,
    });
  }
  const spacingValue = Number(raw.spacingValue);
  const nextId = Number(raw.nextId);
  const nextGroupId = Number(raw.nextGroupId);
  const updatedAt = Number(raw.updatedAt);
  return {
    id: typeof raw.id === "string" && raw.id ? raw.id : makeSceneId(),
    name: typeof raw.name === "string" && raw.name.trim().length ? raw.name.trim().slice(0, 80) : fallbackName,
    spacingValue: Number.isFinite(spacingValue) ? normalizeSpacingValue(spacingValue, unit) : normalizeSpacingValue(1, unit),
    unit,
    boxes,
    nextId: Number.isFinite(nextId) ? Math.max(1, nextId) : boxes.reduce((acc, box) => Math.max(acc, box.id + 1), 1),
    nextGroupId: Number.isFinite(nextGroupId) ? Math.max(1, nextGroupId) : boxes.reduce((acc, box) => Math.max(acc, box.groupId + 1), 1),
    updatedAt: Number.isFinite(updatedAt) ? updatedAt : Date.now(),
  };
}

function createDefaultSceneStore(): SceneStore {
  const scene = createSceneData({ name: "Scene 1" });
  return { scenes: [scene], activeId: scene.id };
}

function loadStoredSceneState(): SceneStore | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(SCENE_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    const scenesInput = Array.isArray(parsed.scenes) ? parsed.scenes : [];
    const scenes: SceneData[] = [];
    scenesInput.forEach((entry: unknown, idx: number) => {
      const scene = sanitizeSceneData(entry, `Scene ${idx + 1}`);
      if (scene) scenes.push(scene);
    });
    if (!scenes.length) return null;
    const requestedActive = typeof parsed.activeId === "string" ? parsed.activeId : null;
    const fallbackId = scenes[0]!.id;
    const activeId = requestedActive && scenes.some(scene => scene.id === requestedActive)
      ? requestedActive
      : fallbackId;
    return { scenes, activeId };
  } catch (error) {
    console.warn("Failed to load scenes from storage", error);
    return null;
  }
}

function persistSceneStore(state: SceneStore) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(SCENE_STORAGE_KEY, JSON.stringify(state));
  } catch (error) {
    console.warn("Failed to persist scenes", error);
  }
}

function useGridPositions(spacingMeters: number, baseExtent = 30, maxPoints = 80000) {
  return useMemo(() => {
    const s = Math.max(0.05, spacingMeters);

    const desiredPerAxis = Math.floor(Math.cbrt(maxPoints));
    const maxExtentByPoints = (desiredPerAxis * s) / 2;
    const extent = Math.min(baseExtent, maxExtentByPoints);

    const n = Math.max(1, Math.floor(extent / s));
    const countPerAxis = n * 2 + 1;

    const planeCount = countPerAxis * countPerAxis;
    const otherCount = planeCount * (countPerAxis - 1);

    const ground = new Float32Array(planeCount * 3);
    const other = new Float32Array(otherCount * 3);

    let gi = 0;
    let oi = 0;
    for (let yi = -n; yi <= n; yi++) {
      const y = yi * s;
      const isGround = yi === 0;
      for (let xi = -n; xi <= n; xi++) {
        const x = xi * s;
        for (let zi = -n; zi <= n; zi++) {
          const z = zi * s;
          if (isGround) {
            ground[gi++] = x;
            ground[gi++] = 0;
            ground[gi++] = z;
          } else {
            other[oi++] = x;
            other[oi++] = y;
            other[oi++] = z;
          }
        }
      }
    }

    return {
      ground,
      other,
      stats: {
        total: planeCount + otherCount,
        plane: planeCount,
        other: otherCount,
        extent,
        countPerAxis,
      },
    };
  }, [spacingMeters, baseExtent, maxPoints]);
}

function makeCircleTexture(): THREE.Texture {
  const size = 64;
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d")!;
  ctx.clearRect(0, 0, size, size);
  ctx.fillStyle = "#fff";
  ctx.beginPath();
  ctx.arc(size / 2, size / 2, size * 0.45, 0, Math.PI * 2);
  ctx.fill();
  const texture = new THREE.CanvasTexture(canvas);
  texture.needsUpdate = true;
  return texture;
}

function GridPoints({ spacingMeters, isDark }: { spacingMeters: number; isDark: boolean }) {
  const { ground, other } = useGridPositions(spacingMeters);

  const [circleMap] = useState(() => (typeof document !== "undefined" ? makeCircleTexture() : null));

  const groundGeom = useMemo(() => {
    const geom = new THREE.BufferGeometry();
    geom.setAttribute("position", new THREE.BufferAttribute(ground, 3));
    return geom;
  }, [ground]);

  const otherGeom = useMemo(() => {
    const geom = new THREE.BufferGeometry();
    geom.setAttribute("position", new THREE.BufferAttribute(other, 3));
    return geom;
  }, [other]);

  return (
    <>
      <points>
        <primitive object={groundGeom} attach="geometry" />
        <pointsMaterial
          size={0.08}
          sizeAttenuation
          color={isDark ? "#86efac" : "#0ea5e9"}
          transparent
          opacity={isDark ? 0.6 : 0.55}
          depthWrite={false}
          map={circleMap as any}
          alphaMap={circleMap as any}
          alphaTest={0.1}
        />
      </points>

      <points>
        <primitive object={otherGeom} attach="geometry" />
        <pointsMaterial
          size={0.07}
          sizeAttenuation
          color={isDark ? "#93c5fd" : "#1f2937"}
          transparent
          opacity={isDark ? 0.22 : 0.18}
          depthWrite={false}
          map={circleMap as any}
          alphaMap={circleMap as any}
          alphaTest={0.1}
        />
      </points>
    </>
  );
}

// Resizable container for MarkPad
function MarkPadContainer({
  lines,
  evaluation,
  onLinesChange,
  onResetTemplate,
}: {
  lines: string[];
  evaluation: MarkEvaluation;
  onLinesChange: (lines: string[]) => void;
  onResetTemplate: () => void;
}) {
  const [width, setWidth] = useState(() => Math.round(window.innerWidth * 0.4));
  const isDragging = useRef(false);
  const startX = useRef(0);
  const startWidth = useRef(width);
  const minWidth = 320;
  const maxWidth = Math.round(window.innerWidth * 0.8);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    isDragging.current = true;
    startX.current = e.clientX;
    startWidth.current = width;
    document.body.style.cursor = "ew-resize";
    document.body.style.userSelect = "none";
  }, [width]);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging.current) return;
      const delta = startX.current - e.clientX;
      const newWidth = Math.min(maxWidth, Math.max(minWidth, startWidth.current + delta));
      setWidth(newWidth);
    };

    const handleMouseUp = () => {
      isDragging.current = false;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, []);

  return (
    <div 
      className="pointer-events-none absolute top-24 right-4 bottom-4 z-20 flex"
      style={{ width: `${width}px` }}
    >
      {/* Drag handle on left edge */}
      <div
        className="pointer-events-auto absolute left-0 top-0 bottom-0 w-2 cursor-ew-resize z-10 group"
        onMouseDown={handleMouseDown}
      >
        <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-16 rounded-full bg-muted-foreground/30 group-hover:bg-muted-foreground/60 transition-colors" />
      </div>
      <div className="pointer-events-auto w-full h-full pl-2">
        <MarkPad
          lines={lines}
          evaluation={evaluation}
          onLinesChange={onLinesChange}
          onResetTemplate={onResetTemplate}
        />
      </div>
    </div>
  );
}

export function GridScene() {
  const initialSceneState = useMemo<SceneStore>(() => loadStoredSceneState() ?? createDefaultSceneStore(), []);
  const initialActiveScene = initialSceneState.scenes.find(scene => scene.id === initialSceneState.activeId)
    ?? initialSceneState.scenes[0]!;
  const [sceneState, setSceneState] = useState<SceneStore>(initialSceneState);
  const [workspaceSettings, setWorkspaceSettings] = useState<WorkspaceSettings>(() => loadWorkspaceSettings());
  const markModeEnabled = workspaceSettings.markModeEnabled;
  const markLines = workspaceSettings.markLines;
  const [spacingValue, setSpacingValue] = useState(() => initialActiveScene.spacingValue);
  const [unit, setUnit] = useState<Unit>(initialActiveScene.unit);
  const [sceneUnitDraft, setSceneUnitDraft] = useState<Unit>(initialActiveScene.unit);
  const [sceneSpacingDraft, setSceneSpacingDraft] = useState(() => String(initialActiveScene.spacingValue));
  const spacingConfig = UNIT_CONFIG[unit];
  const spacingPrecision = spacingConfig.precision;
  const spacingMeters = toMeters(spacingValue, unit);
  const draftSpacingConfig = UNIT_CONFIG[sceneUnitDraft];
  const menuSpacingMin = draftSpacingConfig.min;
  const menuSpacingStep = draftSpacingConfig.step;
  const { isDark } = useTheme();
  const controlsRef = useRef<OrbitControlsImpl | null>(null);
  const VISIBLE_CELLS = 60; // keep roughly this many cells visible across spacings
  const gridFadeDistance = spacingMeters * VISIBLE_CELLS;
  const markEvaluation = useMemo<MarkEvaluation>(() => evaluateMarkLines(markLines), [markLines]);

  const updateWorkspaceSettings = useCallback((updater: (prev: WorkspaceSettings) => WorkspaceSettings) => {
    setWorkspaceSettings(prev => {
      const next = updater(prev);
      persistWorkspaceSettings(next);
      return next;
    });
  }, []);

  // Keep grid unit aligned with mark-mode primary unit and spacing
  useEffect(() => {
    if (!markModeEnabled) return;
    const primary = markEvaluation.unitLabel;
    if (primary !== "m" && primary !== "ft") return;
    
    // Use the grid spacing from the mark evaluation (value of first unit)
    const markSpacing = markEvaluation.gridSpacing;
    const normalizedSpacing = normalizeSpacingValue(markSpacing, primary);
    
    if (unit === primary && spacingValue === normalizedSpacing) return;
    
    setUnit(primary);
    setSceneUnitDraft(primary);
    setSpacingValue(normalizedSpacing);
    setSceneSpacingDraft(String(normalizedSpacing));
  }, [markModeEnabled, markEvaluation.unitLabel, markEvaluation.gridSpacing]);

  const toggleMarkMode = () => {
    updateWorkspaceSettings(prev => ({ ...prev, markModeEnabled: !prev.markModeEnabled }));
  };

  const handleMarkLinesChange = (newLines: string[]) => {
    updateWorkspaceSettings(prev => ({ ...prev, markLines: newLines }));
  };

  const handleResetMarkTemplate = () => {
    updateWorkspaceSettings(prev => ({ ...prev, markLines: DEFAULT_MARK_LINES }));
  };

  const handleSpacingInputChange = (rawValue: string) => {
    setSceneSpacingDraft(rawValue);
  };

  const normalizeSceneSpacingDraft = useCallback((rawValue?: string): number => {
    const candidate = rawValue ?? sceneSpacingDraft;
    const trimmed = typeof candidate === "string" ? candidate.trim() : "";
    const parsed = parseFloat(trimmed);
    const normalized = normalizeSpacingValue(
      Number.isFinite(parsed) ? parsed : UNIT_CONFIG[sceneUnitDraft].min,
      sceneUnitDraft,
    );
    setSceneSpacingDraft(String(normalized));
    return normalized;
  }, [sceneSpacingDraft, sceneUnitDraft]);

  const revertSpacingDraftToSaved = useCallback(() => {
    const savedMeters = toMeters(spacingValue, unit);
    const savedInDraftUnit = fromMeters(savedMeters, sceneUnitDraft);
    const normalized = normalizeSpacingValue(savedInDraftUnit, sceneUnitDraft);
    setSceneSpacingDraft(String(normalized));
    return normalized;
  }, [sceneUnitDraft, spacingValue, unit]);

  const handleSceneUnitDraftChange = (nextUnit: Unit) => {
    if (nextUnit === sceneUnitDraft) return;
    const currentNormalized = normalizeSceneSpacingDraft();
    const meters = toMeters(currentNormalized, sceneUnitDraft);
    const converted = fromMeters(meters, nextUnit);
    const normalized = normalizeSpacingValue(converted, nextUnit);
    setSceneUnitDraft(nextUnit);
    setSceneSpacingDraft(String(normalized));
  };

  const handleSceneMenuSave = () => {
    const normalizedSpacing = normalizeSceneSpacingDraft();
    setSpacingValue(normalizedSpacing);
    setUnit(sceneUnitDraft);
    handleRenameScene(sceneNameDraft);
    closeSceneMenu({ resetDrafts: false });
  };

  // Boxes state
  type ClipboardEntry = {
    position: [number, number, number];
    size: [number, number, number];
    color: string;
    groupId: number;
  };
  type ContactInfo = {
    left: boolean;
    right: boolean;
    front: boolean;
    back: boolean;
    bottom: boolean;
    top: boolean;
  };

  type MetricsSnapshot = {
    totals: {
      count: number;
      volumeMeters3: number;
      areaMeters2: number;
    };
    groups: Array<{
      groupId: number;
      color: string;
      count: number;
      volumeMeters3: number;
      areaMeters2: number;
    }>;
  };

  type FormattedMetrics = {
    totals: MetricsSnapshot["totals"] & {
      volume: number;
      area: number;
      volumeDisplay: string;
      areaDisplay: string;
    };
    groups: Array<MetricsSnapshot["groups"][number] & {
      volume: number;
      area: number;
      volumeDisplay: string;
      areaDisplay: string;
    }>;
  };

  const boxVolume = (box: BoxItem) => box.size[0] * box.size[1] * box.size[2];

  function computeFootprintArea(items: BoxItem[]): number {
    if (!items.length) return 0;
    type Event = { x: number; z1: number; z2: number; delta: 1 | -1 };
    const events: Event[] = [];
    const zCoords = new Set<number>();

    for (const box of items) {
      const halfW = box.size[0] / 2;
      const halfD = box.size[2] / 2;
      const x1 = box.position[0] - halfW;
      const x2 = box.position[0] + halfW;
      const z1 = box.position[2] - halfD;
      const z2 = box.position[2] + halfD;
      if (x1 === x2 || z1 === z2) continue;
      events.push({ x: x1, z1, z2, delta: 1 });
      events.push({ x: x2, z1, z2, delta: -1 });
      zCoords.add(z1);
      zCoords.add(z2);
    }

    if (!events.length) return 0;

    const sortedZ = Array.from(zCoords).sort((a, b) => a - b);
    if (sortedZ.length < 2) return 0;

    const zIndex = new Map<number, number>();
    for (let i = 0; i < sortedZ.length; i++) {
      zIndex.set(sortedZ[i]!, i);
    }

    const segmentWidths: number[] = [];
    for (let i = 0; i < sortedZ.length - 1; i++) {
      segmentWidths.push(sortedZ[i + 1]! - sortedZ[i]!);
    }

    const coverage = new Array(sortedZ.length - 1).fill(0);

    events.sort((a, b) => (a.x === b.x ? b.delta - a.delta : a.x - b.x));

    let area = 0;
    let prevX = events[0]!.x;
    let idx = 0;

    while (idx < events.length) {
      const currentX = events[idx]!.x;
      const width = currentX - prevX;
      if (width > 0) {
        let coveredZ = 0;
        for (let i = 0; i < coverage.length; i++) {
          if (coverage[i]! > 0) coveredZ += segmentWidths[i]!;
        }
        area += coveredZ * width;
      }

      while (idx < events.length && events[idx]!.x === currentX) {
        const ev = events[idx]!;
        const start = zIndex.get(ev.z1);
        const end = zIndex.get(ev.z2);
        if (start != null && end != null) {
          for (let i = start; i < end; i++) {
            coverage[i]! += ev.delta;
          }
        }
        idx++;
      }
      prevX = currentX;
    }

    return area;
  }

  const [boxes, setBoxes] = useState<BoxItem[]>(() => cloneBoxes(initialActiveScene.boxes));
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [showHelp, setShowHelp] = useState(false);
  const nextIdRef = useRef(initialActiveScene.nextId);
  const nextGroupIdRef = useRef(initialActiveScene.nextGroupId);
  const widthInputRef = useRef<HTMLInputElement | null>(null);
  const clipboardRef = useRef<ClipboardEntry[] | null>(null);
  type ResizeFeedback = { message: string; conflictingIds: number[]; ts: number };
  const [resizeFeedback, setResizeFeedback] = useState<ResizeFeedback | null>(null);
  const shakingIdsRef = useRef<Map<number, { expires: number; started: number }>>(new Map());
  const [isSceneMenuOpen, setIsSceneMenuOpen] = useState(false);
  const [sceneNameDraft, setSceneNameDraft] = useState(initialActiveScene.name);
  const sceneMenuRef = useRef<HTMLDivElement | null>(null);
  const sceneMenuButtonRef = useRef<HTMLButtonElement | null>(null);
  const activeScene = sceneState.scenes.find(scene => scene.id === sceneState.activeId) ?? null;

  const resetSceneDrafts = useCallback(() => {
    setSceneNameDraft(activeScene?.name ?? "");
    setSceneUnitDraft(unit);
    setSceneSpacingDraft(String(spacingValue));
  }, [activeScene?.name, spacingValue, unit]);

  const closeSceneMenu = useCallback((options?: { resetDrafts?: boolean }) => {
    if (options?.resetDrafts !== false) {
      resetSceneDrafts();
    }
    setIsSceneMenuOpen(false);
  }, [resetSceneDrafts]);

  const setSceneStateAndPersist = useCallback((updater: (prev: SceneStore) => SceneStore) => {
    setSceneState(prev => {
      const next = updater(prev);
      if (next === prev) return prev;
      persistSceneStore(next);
      return next;
    });
  }, []);

  const buildSceneSnapshot = useCallback((scene: SceneData): SceneData | null => {
    const boxesChanged = !areBoxesEqual(scene.boxes, boxes);
    const spacingChanged = scene.spacingValue !== spacingValue;
    const unitChanged = scene.unit !== unit;
    const idChanged = scene.nextId !== nextIdRef.current || scene.nextGroupId !== nextGroupIdRef.current;
    if (!boxesChanged && !spacingChanged && !unitChanged && !idChanged) return null;
    return {
      ...scene,
      spacingValue,
      unit,
      boxes: boxesChanged ? cloneBoxes(boxes) : scene.boxes,
      nextId: nextIdRef.current,
      nextGroupId: nextGroupIdRef.current,
      updatedAt: Date.now(),
    };
  }, [boxes, spacingValue, unit]);

  const patchActiveScene = useCallback((mutator: (scene: SceneData) => SceneData | null) => {
    setSceneStateAndPersist(prev => {
      const idx = prev.scenes.findIndex(scene => scene.id === prev.activeId);
      if (idx === -1) return prev;
      const current = prev.scenes[idx]!;
      const updated = mutator(current);
      if (!updated || updated === current) return prev;
      const nextScenes = [...prev.scenes];
      nextScenes[idx] = updated;
      return { ...prev, scenes: nextScenes };
    });
  }, [setSceneStateAndPersist]);

  const hydrateScene = useCallback((scene: SceneData) => {
    setSpacingValue(scene.spacingValue);
    setUnit(scene.unit);
    setBoxes(cloneBoxes(scene.boxes));
    nextIdRef.current = scene.nextId;
    nextGroupIdRef.current = scene.nextGroupId;
    setSelectedId(null);
    setSelectedIds(new Set());
  }, [setBoxes, setSelectedId, setSelectedIds, setSpacingValue, setUnit]);

  const handleCreateScene = () => {
    const template = createSceneData({
      name: generateSceneName(sceneState.scenes),
      spacingValue,
      unit,
      boxes: [],
      nextId: 1,
      nextGroupId: 1,
    });
    setSceneStateAndPersist(prev => {
      const nextScenes = [...prev.scenes];
      const currentIdx = nextScenes.findIndex(scene => scene.id === prev.activeId);
      if (currentIdx !== -1) {
        const snapshotTarget = nextScenes[currentIdx];
        if (snapshotTarget) {
          const snapshot = buildSceneSnapshot(snapshotTarget);
          if (snapshot) nextScenes[currentIdx] = snapshot;
        }
      }
      nextScenes.push(template);
      return { scenes: nextScenes, activeId: template.id };
    });
    hydrateScene(template);
  };

  const handleRenameScene = (rawName?: string | null) => {
    const current = sceneState.scenes.find(scene => scene.id === sceneState.activeId);
    if (!current) return;
    let candidate = rawName;
    if (candidate == null) {
      if (typeof window === "undefined") return;
      candidate = window.prompt("Scene name", current.name);
    }
    if (candidate == null) return;
    const trimmed = candidate.trim();
    if (!trimmed || trimmed === current.name) return;
    patchActiveScene(scene => (scene.name === trimmed ? null : { ...scene, name: trimmed }));
    setSceneNameDraft(trimmed);
  };

  const handleDeleteScene = () => {
    if (sceneState.scenes.length <= 1) {
      if (typeof window !== "undefined") {
        window.alert("You need at least one scene.");
      }
      return;
    }
    const activeIdx = sceneState.scenes.findIndex(scene => scene.id === sceneState.activeId);
    if (activeIdx === -1) return;
    const activeScene = sceneState.scenes[activeIdx]!;
    const confirmed = typeof window === "undefined" ? true : window.confirm(`Delete "${activeScene.name}"?`);
    if (!confirmed) return;
    const remainingScenes = sceneState.scenes.filter(scene => scene.id !== sceneState.activeId);
    const nextScene = remainingScenes[Math.min(activeIdx, remainingScenes.length - 1)];
    if (!nextScene) return;
    setSceneStateAndPersist(prev => {
      const filtered = prev.scenes.filter(scene => scene.id !== prev.activeId);
      if (!filtered.length) {
        const fallback = createSceneData({ name: "Scene 1" });
        return { scenes: [fallback], activeId: fallback.id };
      }
      const fallbackScene = filtered[Math.min(activeIdx, filtered.length - 1)] ?? filtered[0]!;
      return { scenes: filtered, activeId: fallbackScene.id };
    });
    hydrateScene(nextScene);
  };

  const handleSceneMenuSelect = (value: string) => {
    if (value === SCENE_ACTION_CREATE) {
      handleCreateScene();
      return;
    }
    if (value === sceneState.activeId) return;
    const targetScene = sceneState.scenes.find(scene => scene.id === value);
    if (!targetScene) return;
    setSceneStateAndPersist(prev => {
      const nextScenes = [...prev.scenes];
      const currentIdx = nextScenes.findIndex(scene => scene.id === prev.activeId);
      if (currentIdx !== -1) {
        const snapshotTarget = nextScenes[currentIdx];
        if (snapshotTarget) {
          const snapshot = buildSceneSnapshot(snapshotTarget);
          if (snapshot) nextScenes[currentIdx] = snapshot;
        }
      }
      return { scenes: nextScenes, activeId: value };
    });
    hydrateScene(targetScene);
  };

  useEffect(() => {
    patchActiveScene(scene => buildSceneSnapshot(scene));
  }, [buildSceneSnapshot, patchActiveScene, sceneState.activeId]);

  useEffect(() => {
    setSceneNameDraft(activeScene?.name ?? "");
  }, [activeScene?.id, activeScene?.name]);

  useEffect(() => {
    setSceneSpacingDraft(String(spacingValue));
  }, [spacingValue]);

  useEffect(() => {
    setSceneUnitDraft(unit);
  }, [unit]);

  useEffect(() => {
    if (!isSceneMenuOpen) return;
    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node | null;
      if (target && sceneMenuRef.current?.contains(target)) return;
      if (target && sceneMenuButtonRef.current?.contains(target)) return;
      if (target instanceof HTMLElement && target.closest('[data-scene-menu-portal="true"]')) return;
      closeSceneMenu();
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") closeSceneMenu();
    };
    window.addEventListener("mousedown", handlePointerDown);
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("mousedown", handlePointerDown);
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [closeSceneMenu, isSceneMenuOpen]);

  const palette = [
    "#ef4444",
    "#f59e0b",
    "#10b981",
    "#3b82f6",
    "#8b5cf6",
    "#ec4899",
    "#14b8a6",
    "#22c55e",
  ];

  const selectedBox = useMemo(() => boxes.find(b => b.id === selectedId) || null, [boxes, selectedId]);
  const dimensionStep = roundTo(spacingValue, spacingPrecision);

  const applySelection = useCallback((ids: Set<number>, preferredId: number | null = null) => {
    let nextPrimary: number | null = null;
    if (ids.size > 0) {
      if (preferredId != null && ids.has(preferredId)) {
        nextPrimary = preferredId;
      } else {
        nextPrimary = ids.values().next().value ?? null;
      }
    }
    setSelectedIds(ids);
    setSelectedId(nextPrimary);
    return ids;
  }, [setSelectedIds, setSelectedId]);

  const selectBox = (boxId: number, additive: boolean) => {
    let nextSet: Set<number>;
    if (additive) {
      nextSet = new Set(selectedIds);
      if (nextSet.has(boxId)) {
        nextSet.delete(boxId);
      } else {
        nextSet.add(boxId);
      }
    } else {
      nextSet = new Set([boxId]);
    }
    const preferredId = nextSet.has(boxId) ? boxId : null;
    return applySelection(nextSet, preferredId);
  };

  const clearSelection = useCallback(() => {
    applySelection(new Set(), null);
  }, [applySelection]);

  const selectedBoxesList = useMemo(() => {
    if (selectedIds.size === 0) return [];
    const lookup = new Set(selectedIds);
    return boxes.filter(b => lookup.has(b.id));
  }, [boxes, selectedIds]);

  const selectionSummary = useMemo(() => {
    if (selectedBoxesList.length <= 1) return null;
    let minX = Number.POSITIVE_INFINITY;
    let maxX = Number.NEGATIVE_INFINITY;
    let minY = Number.POSITIVE_INFINITY;
    let maxY = Number.NEGATIVE_INFINITY;
    let minZ = Number.POSITIVE_INFINITY;
    let maxZ = Number.NEGATIVE_INFINITY;
    for (const box of selectedBoxesList) {
      const [x, y, z] = box.position;
      const [w, h, d] = box.size;
      minX = Math.min(minX, x - w / 2);
      maxX = Math.max(maxX, x + w / 2);
      minY = Math.min(minY, y - h / 2);
      maxY = Math.max(maxY, y + h / 2);
      minZ = Math.min(minZ, z - d / 2);
      maxZ = Math.max(maxZ, z + d / 2);
    }
    const width = Math.max(0, maxX - minX);
    const height = Math.max(0, maxY - minY);
    const depth = Math.max(0, maxZ - minZ);
    const totalVolume = selectedBoxesList.reduce((sum, box) => sum + boxVolume(box), 0);
    const areaMeters = computeFootprintArea(selectedBoxesList);
    const groups = new Set<number>();
    for (const box of selectedBoxesList) {
      groups.add(box.groupId);
    }
    const lengthDisplay = (value: number) => `${roundTo(fromMeters(value, unit), spacingPrecision)} ${unit}`;
    return {
      count: selectedBoxesList.length,
      groupCount: groups.size,
      volumeDisplay: formatMetricWithUnit(fromMetersCubed(totalVolume, unit), VOLUME_UNIT_LABEL[unit]),
      areaDisplay: formatMetricWithUnit(fromMetersSquared(areaMeters, unit), AREA_UNIT_LABEL[unit]),
      widthDisplay: lengthDisplay(width),
      depthDisplay: lengthDisplay(depth),
      heightDisplay: lengthDisplay(height),
    };
  }, [selectedBoxesList, unit, spacingPrecision]);

  const metrics = useMemo<MetricsSnapshot | null>(() => {
    if (boxes.length === 0) return null;

    const totalVolumeMeters3 = boxes.reduce((sum, box) => sum + boxVolume(box), 0);
    const totalAreaMeters2 = computeFootprintArea(boxes);

    const groupBuckets = new Map<number, BoxItem[]>();
    for (const box of boxes) {
      const current = groupBuckets.get(box.groupId);
      if (current) current.push(box);
      else groupBuckets.set(box.groupId, [box]);
    }

    const groups = Array.from(groupBuckets.entries())
      .map(([groupId, groupBoxes]) => ({
        groupId,
        color: groupBoxes[0]?.color ?? "#999999",
        count: groupBoxes.length,
        volumeMeters3: groupBoxes.reduce((sum, box) => sum + boxVolume(box), 0),
        areaMeters2: computeFootprintArea(groupBoxes),
      }))
      .sort((a, b) => a.groupId - b.groupId);

    return {
      totals: {
        count: boxes.length,
        volumeMeters3: totalVolumeMeters3,
        areaMeters2: totalAreaMeters2,
      },
      groups,
    };
  }, [boxes]);

  const formattedMetrics = useMemo<FormattedMetrics | null>(() => {
    if (!metrics) return null;

    const totalsVolume = fromMetersCubed(metrics.totals.volumeMeters3, unit);
    const totalsArea = fromMetersSquared(metrics.totals.areaMeters2, unit);

    const groups = metrics.groups.map(group => {
      const groupVolume = fromMetersCubed(group.volumeMeters3, unit);
      const groupArea = fromMetersSquared(group.areaMeters2, unit);
      return {
        ...group,
        volume: groupVolume,
        area: groupArea,
        volumeDisplay: formatMetricWithUnit(groupVolume, VOLUME_UNIT_LABEL[unit]),
        areaDisplay: formatMetricWithUnit(groupArea, AREA_UNIT_LABEL[unit]),
      };
    });

    return {
      totals: {
        ...metrics.totals,
        volume: totalsVolume,
        area: totalsArea,
        volumeDisplay: formatMetricWithUnit(totalsVolume, VOLUME_UNIT_LABEL[unit]),
        areaDisplay: formatMetricWithUnit(totalsArea, AREA_UNIT_LABEL[unit]),
      },
      groups,
    };
  }, [metrics, unit]);

  const normalizedDraftSpacingValue = useMemo(() => {
    const trimmed = sceneSpacingDraft.trim();
    const parsed = parseFloat(trimmed);
    return normalizeSpacingValue(
      Number.isFinite(parsed) ? parsed : UNIT_CONFIG[sceneUnitDraft].min,
      sceneUnitDraft,
    );
  }, [sceneSpacingDraft, sceneUnitDraft]);

  const draftSpacingMeters = useMemo(() => (
    toMeters(normalizedDraftSpacingValue, sceneUnitDraft)
  ), [normalizedDraftSpacingValue, sceneUnitDraft]);

  const spacingChanged = Math.abs(draftSpacingMeters - spacingMeters) > 1e-6;
  const trimmedSceneName = sceneNameDraft.trim();
  const hasSceneNameChange = trimmedSceneName !== (activeScene?.name ?? "");
  const sceneMenuHasChanges = hasSceneNameChange || sceneUnitDraft !== unit || spacingChanged;
  const sceneMenuSaveDisabled = !trimmedSceneName || !sceneMenuHasChanges;

  useEffect(() => {
    if (selectedBox && widthInputRef.current) {
      // focus first field when a box becomes selected
      widthInputRef.current.focus();
      widthInputRef.current.select();
    }
  }, [selectedBox?.id]);

  const isEditableTarget = (target: EventTarget | null) => {
    if (typeof window === "undefined") return false;
    const el = target as HTMLElement | null;
    if (!el) return false;
    const tag = el.tagName;
    if (tag === "INPUT" || tag === "TEXTAREA") return true;
    if ((el as any).isContentEditable) return true;
    return false;
  };

  // Delete helper: deletes multi-selected if any, otherwise the single selectedId
  const deleteSelected = () => {
    const idsToDelete = new Set<number>(selectedIds);
    if (idsToDelete.size === 0 && selectedId != null) idsToDelete.add(selectedId);
    if (idsToDelete.size === 0) return;
    setBoxes(prev => prev.filter(b => !idsToDelete.has(b.id)));
    idsToDelete.forEach(id => physicsMapRef.current.delete(id));
    setSelectedId(null);
    setSelectedIds(new Set());
  };

  // Keyboard delete/backspace
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Delete" && e.key !== "Backspace") return;
      if (isEditableTarget(e.target)) return;
      if (selectedId == null && selectedIds.size === 0) return;
      e.preventDefault();
      deleteSelected();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [selectedId, selectedIds]);

  const snap = (v: number, s: number) => Math.round(v / s) * s;
  const snapCenterToEdges = (center: number, size: number, s: number) => snap(center - size / 2, s) + size / 2;

  const alignCenterForResize = (
    center: number,
    currentSize: number,
    newSize: number,
    spacing: number,
  ) => {
    const tolerance = 1e-6;
    const minEdge = center - currentSize / 2;
    const maxEdge = center + currentSize / 2;
    const fallback = snapCenterToEdges(center, newSize, spacing);
    const candidates = [
      snap(minEdge, spacing) + newSize / 2,
      snap(maxEdge, spacing) - newSize / 2,
      fallback,
    ];
    const filtered: number[] = [];
    for (const candidate of candidates) {
      if (!Number.isFinite(candidate)) continue;
      if (filtered.some(existing => Math.abs(existing - candidate) < tolerance)) continue;
      const candMin = candidate - newSize / 2;
      const candMax = candidate + newSize / 2;
      const minAligned = Math.abs(snap(candMin, spacing) - candMin) < tolerance;
      const maxAligned = Math.abs(snap(candMax, spacing) - candMax) < tolerance;
      if (minAligned && maxAligned) filtered.push(candidate);
    }
    if (!filtered.length) return fallback;
    filtered.sort((a, b) => Math.abs(a - center) - Math.abs(b - center));
    return filtered[0] ?? fallback;
  };

  const scheduleResizeFeedbackClear = (ts: number, delayMs = 2400) => {
    if (typeof window === "undefined") return;
    window.setTimeout(() => {
      setResizeFeedback(prev => (prev && prev.ts === ts ? null : prev));
    }, delayMs);
  };

  const triggerBlockShake = (ids: Iterable<number>, durationMs = 480) => {
    const now = typeof performance !== "undefined" ? performance.now() : Date.now();
    const expiresAt = now + durationMs;
    const map = shakingIdsRef.current;
    for (const id of ids) {
      if (!Number.isFinite(id)) continue;
      map.set(id, { started: now, expires: expiresAt });
    }
  };

  const triggerResizeBlocked = (message: string, conflictingIds: number[]) => {
    const ts = typeof performance !== "undefined" ? performance.now() : Date.now();
    setResizeFeedback({ message, conflictingIds, ts });
    scheduleResizeFeedbackClear(ts);
    triggerBlockShake(new Set(conflictingIds));
  };


  // Ensure existing boxes align their edges to grid lines on spacing changes
  useEffect(() => {
    physicsMapRef.current.clear();
    setBoxes(prev => prev.map(b => {
      const nx = snapCenterToEdges(b.position[0], b.size[0], spacingMeters);
      const nz = snapCenterToEdges(b.position[2], b.size[2], spacingMeters);
      if (nx === b.position[0] && nz === b.position[2]) return b;
      return { ...b, position: [nx, b.position[1], nz] };
    }));
  }, [spacingMeters]);

  // --- Intersection helpers (3D AABB) ---
  const overlaps1D = (ac: number, ah: number, bc: number, bh: number) => Math.abs(ac - bc) < (ah + bh) - 1e-8; // allow touching
  const overlaps3D = (
    aPos: [number, number, number], aSize: [number, number, number],
    bPos: [number, number, number], bSize: [number, number, number]
  ) => {
    const [ax, ay, az] = aPos; const [aw, ah, ad] = aSize;
    const [bx, by, bz] = bPos; const [bw, bh, bd] = bSize;
    return overlaps1D(ax, aw / 2, bx, bw / 2)
      && overlaps1D(ay, ah / 2, by, bh / 2)
      && overlaps1D(az, ad / 2, bz, bd / 2);
  };

  const findOverlapsAt = (
    pos: [number, number, number],
    size: [number, number, number],
    excludeId?: number | null,
    list: BoxItem[] = boxes,
  ) => {
    const collisions: BoxItem[] = [];
    for (const b of list) {
      if (excludeId != null && b.id === excludeId) continue;
      if (overlaps3D(pos, size, b.position, b.size)) collisions.push(b);
    }
    return collisions;
  };

  const anyOverlapAt = (
    pos: [number, number, number],
    size: [number, number, number],
    excludeId?: number | null,
    list: BoxItem[] = boxes,
  ) => findOverlapsAt(pos, size, excludeId, list).length > 0;

  // Determine if a box is attached to another box via a side face (X or Z), i.e., flush contact with overlap on the other axes.
  function isSideFaceAttached(
    pos: [number, number, number],
    size: [number, number, number],
    list: BoxItem[],
    ignoreId: number | null = null,
    tolerance = Math.max(spacingMeters * 0.08, 0.01),
  ): boolean {
    const [x, y, z] = pos;
    const [w, h, d] = size;
    const hw = w / 2, hh = h / 2, hd = d / 2;
    for (const o of list) {
      if (ignoreId != null && o.id === ignoreId) continue;
      const [ox, oy, oz] = o.position;
      const [ow, oh, od] = o.size;
      const ohw = ow / 2, ohh = oh / 2, ohd = od / 2;

      // Must overlap in Y to be side-attached
      const yOverlap = Math.abs(y - oy) < (hh + ohh) - 1e-8;
      if (!yOverlap) continue;

      // X face contact: |x - ox| ≈ hw + ohw and Z overlaps
      const xFaceGap = Math.abs(Math.abs(x - ox) - (hw + ohw));
      const zOverlap = Math.abs(z - oz) < (hd + ohd) - 1e-8;
      if (xFaceGap <= tolerance && zOverlap) return true;

      // Z face contact: |z - oz| ≈ hd + ohd and X overlaps
      const zFaceGap = Math.abs(Math.abs(z - oz) - (hd + ohd));
      const xOverlap = Math.abs(x - ox) < (hw + ohw) - 1e-8;
      if (zFaceGap <= tolerance && xOverlap) return true;
    }
    return false;
  }

  // Try to find a non-intersecting position near an intended position by checking adjacency to neighbors
  function resolveNonIntersectingPosition(
    intended: [number, number, number],
    size: [number, number, number],
    excludeId?: number | null,
    list: BoxItem[] = boxes,
  ): [number, number, number] | null {
    const [x, y, z] = intended;
    if (!anyOverlapAt(intended, size, excludeId, list)) return intended;

    const candidates: Array<[number, number, number]> = [];

    // Try adjacent to each neighbor (flush on each side)
    for (const o of list) {
      const [ox, _oy, oz] = o.position;
      const [ow, _oh, od] = o.size;
      const w = size[0], d = size[2];
      candidates.push(
        [snapCenterToEdges(ox + (ow / 2 + w / 2), w, spacingMeters), y, snapCenterToEdges(z, d, spacingMeters)],
        [snapCenterToEdges(ox - (ow / 2 + w / 2), w, spacingMeters), y, snapCenterToEdges(z, d, spacingMeters)],
        [snapCenterToEdges(x, w, spacingMeters), y, snapCenterToEdges(oz + (od / 2 + d / 2), d, spacingMeters)],
        [snapCenterToEdges(x, w, spacingMeters), y, snapCenterToEdges(oz - (od / 2 + d / 2), d, spacingMeters)],
      );
    }

    // Also try a small spiral around intended position
    const steps = 2;
    for (let dx = -steps; dx <= steps; dx++) {
      for (let dz = -steps; dz <= steps; dz++) {
        const nx = snapCenterToEdges(x + dx * spacingMeters, size[0], spacingMeters);
        const nz = snapCenterToEdges(z + dz * spacingMeters, size[2], spacingMeters);
        candidates.push([nx, y, nz]);
      }
    }

    // Pick the closest non-overlapping candidate
    let best: [number, number, number] | null = null;
    let bestDist = Infinity;
    for (const c of candidates) {
      if (!anyOverlapAt(c, size, excludeId, list)) {
        const dist = (c[0] - x) * (c[0] - x) + (c[2] - z) * (c[2] - z);
        if (dist < bestDist) {
          best = c;
          bestDist = dist;
        }
      }
    }
    return best;
  }

  function computeSupportHeightAtPosition(
    pos: [number, number, number],
    size: [number, number, number],
    list: BoxItem[],
    ignoreId: number | null = null,
  ): number {
    const halfY = size[1] / 2;
    let supportY = halfY;
    for (const other of list) {
      if (ignoreId != null && other.id === ignoreId) continue;
      const xOverlap = overlaps1D(pos[0], size[0] / 2, other.position[0], other.size[0] / 2);
      const zOverlap = overlaps1D(pos[2], size[2] / 2, other.position[2], other.size[2] / 2);
      if (xOverlap && zOverlap) {
        const candidate = other.position[1] + other.size[1] / 2 + halfY;
        if (candidate > supportY) supportY = candidate;
      }
    }
    return supportY;
  }

  function computeSupportHeightFor(
    id: number,
    pos: [number, number, number],
    size: [number, number, number],
    list: BoxItem[],
  ): number {
    return computeSupportHeightAtPosition(pos, size, list, id);
  }

  function findAdjacentSnapPosition(
    pos: [number, number, number],
    size: [number, number, number],
    others: BoxItem[],
    spacing: number,
    threshold = spacing * 0.4,
    ignoreId: number | null = null,
  ): [number, number, number] {
    let [x, y, z] = pos;
    const [w, _h, d] = size;
    for (const o of others) {
      if (ignoreId != null && o.id === ignoreId) continue;
      const [ox, oy, oz] = o.position;
      const [ow, _oh, od] = o.size;

      // Candidate positions flush to each side of neighbor
      const candidates: Array<[number, number, number]> = [
        [ox + (ow / 2 + w / 2), y, z], // right side of neighbor
        [ox - (ow / 2 + w / 2), y, z], // left side
        [x, y, oz + (od / 2 + d / 2)], // front
        [x, y, oz - (od / 2 + d / 2)], // back
      ];

      for (const [cx, cy, cz] of candidates) {
        const dx = Math.abs(cx - x);
        const dz = Math.abs(cz - z);
        if (dx <= threshold && dz <= threshold) {
          x = dx <= threshold ? cx : x;
          z = dz <= threshold ? cz : z;
          // snap to grid too
          x = snapCenterToEdges(x, w, spacing);
          z = snapCenterToEdges(z, d, spacing);
          return [x, cy, z];
        }
      }
    }
    // Default grid snap
    return [snapCenterToEdges(x, w, spacing), y, snapCenterToEdges(z, d, spacing)];
  }

  function findSideAttachmentCandidate(
    pos: [number, number, number],
    size: [number, number, number],
    others: BoxItem[],
    spacing: number,
    ignoreId: number | null = null,
    tolerance = Math.max(spacing * 0.45, 0.05),
  ): [number, number, number] | null {
    const [x, y, z] = pos;
    const [w, h, d] = size;
    const halfY = h / 2;
    const verticalTolerance = Math.max(spacing * 0.2, 0.01);

  let bestPos: [number, number, number] | null = null;
  let bestScore = Number.POSITIVE_INFINITY;

    for (const other of others) {
      if (ignoreId != null && other.id === ignoreId) continue;
      const [ox, oy, oz] = other.position;
      const [ow, oh, od] = other.size;
      const otherBottom = oy - oh / 2;
      const otherTop = oy + oh / 2;

      const attempt = (
        facePos: [number, number, number],
        axis: "x" | "z",
        gap: number,
      ) => {
        const yCandidates = new Set<number>();
        yCandidates.add(oy);
        yCandidates.add(otherBottom + halfY);
        yCandidates.add(otherTop - halfY);
        yCandidates.add(y);

        for (const candidateY of yCandidates) {
          const snappedY = Math.max(halfY, snapCenterToEdges(candidateY, h, spacing));
          const candidateBottom = snappedY - halfY;
          const candidateTop = snappedY + halfY;
          const overlapY = Math.min(candidateTop, otherTop) - Math.max(candidateBottom, otherBottom);
          if (overlapY < -verticalTolerance) continue;

          const snappedX = axis === "x" ? snapCenterToEdges(facePos[0], w, spacing) : snapCenterToEdges(x, w, spacing);
          const snappedZ = axis === "z" ? snapCenterToEdges(facePos[2], d, spacing) : snapCenterToEdges(z, d, spacing);
          const candidatePos: [number, number, number] = [snappedX, snappedY, snappedZ];
          const score = gap + Math.abs(snappedY - y);
          if (score < bestScore) {
            bestScore = score;
            bestPos = candidatePos;
          }
        }
      };

      // Align along X-facing sides
      const zOverlap = Math.abs(z - oz) <= (d / 2 + od / 2) + tolerance;
      if (zOverlap) {
        const direction = Math.sign(x - ox) || 1;
        const faceX = ox + direction * (ow / 2 + w / 2);
        const gapX = Math.abs(x - faceX);
        if (gapX <= tolerance) {
          attempt([faceX, oy, z], "x", gapX);
        }
      }

      // Align along Z-facing sides
      const xOverlap = Math.abs(x - ox) <= (w / 2 + ow / 2) + tolerance;
      if (xOverlap) {
        const direction = Math.sign(z - oz) || 1;
        const faceZ = oz + direction * (od / 2 + d / 2);
        const gapZ = Math.abs(z - faceZ);
        if (gapZ <= tolerance) {
          attempt([x, oy, faceZ], "z", gapZ);
        }
      }
    }

    if (bestPos) {
      return bestPos;
    }
    return null;
  }

  const computeContactInfo = (
    box: BoxItem,
    list: BoxItem[],
    spacing: number,
  ): ContactInfo => {
    const tolerance = Math.max(spacing * 0.12, 0.015);
    const result: ContactInfo = { left: false, right: false, front: false, back: false, bottom: false, top: false };
    const minX = box.position[0] - box.size[0] / 2;
    const maxX = box.position[0] + box.size[0] / 2;
    const minY = box.position[1] - box.size[1] / 2;
    const maxY = box.position[1] + box.size[1] / 2;
    const minZ = box.position[2] - box.size[2] / 2;
    const maxZ = box.position[2] + box.size[2] / 2;

    for (const other of list) {
      if (other.id === box.id) continue;
      const overlapY = overlaps1D(box.position[1], box.size[1] / 2, other.position[1], other.size[1] / 2);
      const overlapX = overlaps1D(box.position[0], box.size[0] / 2, other.position[0], other.size[0] / 2);
      const overlapZ = overlaps1D(box.position[2], box.size[2] / 2, other.position[2], other.size[2] / 2);

      if (overlapY && overlapZ) {
        const otherMaxX = other.position[0] + other.size[0] / 2;
        const otherMinX = other.position[0] - other.size[0] / 2;
        if (Math.abs(minX - otherMaxX) <= tolerance) result.left = true;
        if (Math.abs(maxX - otherMinX) <= tolerance) result.right = true;
      }
      if (overlapY && overlapX) {
        const otherMaxZ = other.position[2] + other.size[2] / 2;
        const otherMinZ = other.position[2] - other.size[2] / 2;
        if (Math.abs(minZ - otherMaxZ) <= tolerance) result.back = true;
        if (Math.abs(maxZ - otherMinZ) <= tolerance) result.front = true;
      }
      if (overlapX && overlapZ) {
        const otherMaxY = other.position[1] + other.size[1] / 2;
        const otherMinY = other.position[1] - other.size[1] / 2;
        if (Math.abs(minY - otherMaxY) <= tolerance) result.bottom = true;
        if (Math.abs(maxY - otherMinY) <= tolerance) result.top = true;
      }
    }

    if (Math.abs(minY - 0) <= tolerance) result.bottom = true;

    return result;
  };

  const anchoredCenterForResize = (
    box: BoxItem,
    axis: "x" | "y" | "z",
    newSize: number,
    spacing: number,
    contacts: ContactInfo,
  ) => {
    if (axis === "x") {
      const min = box.position[0] - box.size[0] / 2;
      const max = box.position[0] + box.size[0] / 2;
      if (contacts.left && !contacts.right) {
        return snapCenterToEdges(min + newSize / 2, newSize, spacing);
      }
      if (contacts.right && !contacts.left) {
        return snapCenterToEdges(max - newSize / 2, newSize, spacing);
      }
      return alignCenterForResize(box.position[0], box.size[0], newSize, spacing);
    }
    if (axis === "z") {
      const min = box.position[2] - box.size[2] / 2;
      const max = box.position[2] + box.size[2] / 2;
      if (contacts.back && !contacts.front) {
        return snapCenterToEdges(min + newSize / 2, newSize, spacing);
      }
      if (contacts.front && !contacts.back) {
        return snapCenterToEdges(max - newSize / 2, newSize, spacing);
      }
      return alignCenterForResize(box.position[2], box.size[2], newSize, spacing);
    }
    const min = box.position[1] - box.size[1] / 2;
    const max = box.position[1] + box.size[1] / 2;
    if (contacts.bottom && !contacts.top) {
      return Math.max(newSize / 2, snapCenterToEdges(min + newSize / 2, newSize, spacing));
    }
    if (contacts.top && !contacts.bottom) {
      return Math.max(newSize / 2, snapCenterToEdges(max - newSize / 2, newSize, spacing));
    }
    return Math.max(newSize / 2, alignCenterForResize(box.position[1], box.size[1], newSize, spacing));
  };

  function addBoxAt(x: number, z: number) {
  const id = nextIdRef.current++;
  const groupId = nextGroupIdRef.current++;
    const w = spacingMeters;
    const h = spacingMeters;
    const d = spacingMeters;
    // position snapped to grid and possibly neighbor adjacency
    let pos: [number, number, number] = [
      snapCenterToEdges(x, w, spacingMeters),
      h / 2,
      snapCenterToEdges(z, d, spacingMeters),
    ];
    pos = findAdjacentSnapPosition(pos, [w, h, d], boxes, spacingMeters);
    const supportY = computeSupportHeightAtPosition(pos, [w, h, d], boxes);
    pos = [pos[0], supportY, pos[2]];
    // ensure no intersections
    const resolved = resolveNonIntersectingPosition(pos, [w, h, d]);
    if (!resolved && anyOverlapAt(pos, [w, h, d])) {
      console.warn("No space available to place box without intersection.");
      return;
    }
    const finalCandidate = resolved ?? pos;
    const finalSupportY = computeSupportHeightAtPosition(finalCandidate, [w, h, d], boxes);
    const finalPos: [number, number, number] = [finalCandidate[0], finalSupportY, finalCandidate[2]];
    const idx = ((id - 1) % palette.length + palette.length) % palette.length;
    const color = palette[idx] ?? "#999999";
    const box: BoxItem = {
      id,
      position: finalPos,
      size: [w, h, d],
      color,
      groupId,
    };
    setBoxes(prev => [...prev, box]);
    setSelectedId(id);
    setSelectedIds(new Set([id]));
  }

  const pasteClipboardEntries = (entries: ClipboardEntry[]) => {
    if (!entries.length) return;

    const sizeForSpacing = (value: number) => Math.max(spacingMeters, Math.round(value / spacingMeters) * spacingMeters);
    const offsets: Array<[number, number, number]> = [
      [spacingMeters, 0, spacingMeters],
      [spacingMeters, 0, 0],
      [0, 0, spacingMeters],
      [-spacingMeters, 0, spacingMeters],
      [spacingMeters, 0, -spacingMeters],
      [0, 0, -spacingMeters],
      [-spacingMeters, 0, 0],
      [0, 0, 0],
    ];

    let placed: ClipboardEntry[] | null = null;

    for (const [offX, offY, offZ] of offsets) {
      const proposed: ClipboardEntry[] = [];
      let ok = true;

      for (const entry of entries) {
        const size: [number, number, number] = [
          sizeForSpacing(entry.size[0]),
          sizeForSpacing(entry.size[1]),
          sizeForSpacing(entry.size[2]),
        ];
        let candidate: [number, number, number] = [
          entry.position[0] + offX,
          entry.position[1] + offY,
          entry.position[2] + offZ,
        ];
        candidate = [
          snapCenterToEdges(candidate[0], size[0], spacingMeters),
          Math.max(size[1] / 2, snap(candidate[1] - size[1] / 2, spacingMeters) + size[1] / 2),
          snapCenterToEdges(candidate[2], size[2], spacingMeters),
        ];

        const proposedAsBoxes: BoxItem[] = proposed.map((p, idx) => ({
          id: -(idx + 1),
          position: p.position,
          size: p.size,
          color: p.color,
          groupId: p.groupId,
        }));
        const combined: BoxItem[] = [...boxes, ...proposedAsBoxes];

        const resolved = resolveNonIntersectingPosition(candidate, size, null, combined);
        if (resolved) {
          candidate = resolved;
        } else if (anyOverlapAt(candidate, size, null, combined)) {
          ok = false;
          break;
        }

        const sideAttached = isSideFaceAttached(candidate, size, combined, null);
        const supportY = computeSupportHeightAtPosition(candidate, size, combined);
        const finalY = sideAttached ? Math.max(size[1] / 2, candidate[1]) : supportY;
        const finalPosition: [number, number, number] = [candidate[0], finalY, candidate[2]];
        proposed.push({
          position: [finalPosition[0], finalPosition[1], finalPosition[2]],
          size: [size[0], size[1], size[2]],
          color: entry.color,
          groupId: entry.groupId,
        });
      }

      if (ok) {
        placed = proposed;
        break;
      }
    }

    if (!placed || placed.length === 0) return;

    const created: BoxItem[] = placed.map(p => {
      const id = nextIdRef.current++;
        let groupId = p.groupId;
        if (!Number.isFinite(groupId)) {
          groupId = nextGroupIdRef.current++;
        } else {
          nextGroupIdRef.current = Math.max(nextGroupIdRef.current, groupId + 1);
        }
      return {
        id,
        position: [p.position[0], p.position[1], p.position[2]],
        size: [p.size[0], p.size[1], p.size[2]],
        color: p.color,
          groupId,
      } as BoxItem;
    });

    setBoxes(prev => [...prev, ...created]);
    if (created.length === 1) {
      const only = created[0];
      if (only) {
        setSelectedId(only.id);
        setSelectedIds(new Set([only.id]));
      }
    } else {
      setSelectedId(null);
      setSelectedIds(new Set(created.map(b => b.id)));
    }
  };

    const updateGroupBoxes = (
      groupId: number,
      transform: (box: BoxItem, ctx: {
        contacts: ContactInfo;
        spacing: number;
        others: BoxItem[];
        all: BoxItem[];
        group: BoxItem[];
      }) => BoxItem,
      options: { skipCollision?: boolean; context?: string } = {},
    ): boolean => {
      let changed = false;
      setBoxes(prev => {
        const groupBoxes = prev.filter(b => b.groupId === groupId);
        if (!groupBoxes.length) return prev;

        const others = prev.filter(b => b.groupId !== groupId);
        const contactLookup = new Map<number, ContactInfo>();
        for (const box of groupBoxes) {
          contactLookup.set(box.id, computeContactInfo(box, prev, spacingMeters));
        }

        const transformed = groupBoxes.map(box => transform(box, {
          contacts: contactLookup.get(box.id) ?? { left: false, right: false, front: false, back: false, bottom: false, top: false },
          spacing: spacingMeters,
          others,
          all: prev,
          group: groupBoxes,
        }));

        if (!options.skipCollision) {
          let collision: { message: string; ids: number[] } | null = null;

          outer: for (let i = 0; i < transformed.length; i++) {
            const candidate = transformed[i];
            if (!candidate) continue;
            const overlaps = findOverlapsAt(candidate.position, candidate.size, candidate.id, others);
            if (overlaps.length) {
              const contextLabel = options.context ? options.context.toLowerCase() : "size";
              const messageBase = `Cannot change ${contextLabel}`;
              const detail = overlaps.length === 1 ? ": it intersects a nearby block." : ": it intersects multiple nearby blocks.";
              const conflictIds = [candidate.id, ...overlaps.map(o => o.id)];
              collision = { message: `${messageBase}${detail}`, ids: conflictIds };
              break outer;
            }
            for (let j = i + 1; j < transformed.length; j++) {
              const other = transformed[j];
              if (!other) continue;
              if (overlaps3D(candidate.position, candidate.size, other.position, other.size)) {
                const contextLabel = options.context ? options.context.toLowerCase() : "size";
                const messageBase = `Cannot change ${contextLabel}`;
                const conflictIds = [candidate.id, other.id];
                collision = { message: `${messageBase}: selected blocks would intersect each other.`, ids: conflictIds };
                break outer;
              }
            }
          }

          if (collision) {
            const uniqueIds = new Set<number>([...transformed.map(b => b.id), ...collision.ids]);
            triggerResizeBlocked(collision.message, Array.from(uniqueIds));
            return prev;
          }
        }

        transformed.forEach(b => physicsMapRef.current.delete(b.id));
        const lookup = new Map<number, BoxItem>();
        transformed.forEach(b => lookup.set(b.id, b));
        if (resizeFeedback) setResizeFeedback(null);
        changed = true;
        return prev.map(b => lookup.get(b.id) ?? b);
      });
      return changed;
    };

  const getSelectionIds = () => {
    if (selectedIds.size > 0) return new Set(selectedIds);
    if (selectedId != null) return new Set([selectedId]);
    return null;
  };

  const moveSelectionBy = (dx: number, dz: number, explicitIds?: Set<number>) => {
    if (!Number.isFinite(dx) || !Number.isFinite(dz)) return false;
    const ids = explicitIds ?? getSelectionIds();
    if (!ids || ids.size === 0) return false;

    let moved = false;
    setBoxes(prev => {
      const selectedList = prev.filter(b => ids.has(b.id));
      if (!selectedList.length) return prev;
      const others = prev.filter(b => !ids.has(b.id));
      const updated: BoxItem[] = [];

      for (let i = 0; i < selectedList.length; i++) {
        const box = selectedList[i]!;
        const nextX = snapCenterToEdges(box.position[0] + dx, box.size[0], spacingMeters);
        const nextZ = snapCenterToEdges(box.position[2] + dz, box.size[2], spacingMeters);
        const basePos: [number, number, number] = [nextX, box.position[1], nextZ];
        const combined = [
          ...others,
          ...updated,
          ...selectedList.slice(i + 1),
        ];
        const sideAttached = isSideFaceAttached(basePos, box.size, combined, box.id);
        const supportY = computeSupportHeightAtPosition(basePos, box.size, combined, box.id);
        const finalY = sideAttached ? Math.max(box.size[1] / 2, basePos[1]) : supportY;
        updated.push({
          ...box,
          position: [basePos[0], finalY, basePos[2]],
        });
      }

      for (const candidate of updated) {
        const overlaps = findOverlapsAt(candidate.position, candidate.size, candidate.id, others);
        if (overlaps.length) {
          const conflictIds = new Set<number>([candidate.id, ...overlaps.map(o => o.id)]);
          triggerResizeBlocked("Cannot move selection: blocked by nearby block.", Array.from(conflictIds));
          triggerBlockShake(conflictIds);
          return prev;
        }
      }

      for (let i = 0; i < updated.length; i++) {
        for (let j = i + 1; j < updated.length; j++) {
          const a = updated[i]!;
          const b = updated[j]!;
          if (overlaps3D(a.position, a.size, b.position, b.size)) {
            const conflictIds = new Set<number>([a.id, b.id]);
            triggerResizeBlocked("Cannot move selection: selected blocks would intersect.", Array.from(conflictIds));
            triggerBlockShake(conflictIds);
            return prev;
          }
        }
      }

      const lookup = new Map<number, BoxItem>();
      updated.forEach(b => lookup.set(b.id, b));
      if (resizeFeedback) setResizeFeedback(null);
      moved = true;
      return prev.map(b => lookup.get(b.id) ?? b);
    });

    if (moved) {
      ids.forEach(id => physicsMapRef.current.delete(id));
    }

    return moved;
  };

  const moveSelectionByLatestRef = useRef(moveSelectionBy);
  moveSelectionByLatestRef.current = moveSelectionBy;

  const rotateSelection = (mode: "horizontal" | "vertical") => {
    const ids = getSelectionIds();
    if (!ids || ids.size === 0) return false;

    let rotated = false;
    setBoxes(prev => {
      const selectedList = prev.filter(b => ids.has(b.id));
      if (!selectedList.length) return prev;
      const others = prev.filter(b => !ids.has(b.id));
      const updated: BoxItem[] = [];
      const contactMap = new Map<number, ContactInfo>();
      selectedList.forEach(box => {
        contactMap.set(box.id, computeContactInfo(box, prev, spacingMeters));
      });

      for (let i = 0; i < selectedList.length; i++) {
        const box = selectedList[i]!;
        const contacts = contactMap.get(box.id) ?? { left: false, right: false, front: false, back: false, bottom: false, top: false };
        const newSize: [number, number, number] = mode === "horizontal"
          ? [box.size[2], box.size[1], box.size[0]]
          : [box.size[0], box.size[2], box.size[1]];

        const nextX = anchoredCenterForResize(box, "x", newSize[0], spacingMeters, contacts);
        const nextZ = anchoredCenterForResize(box, "z", newSize[2], spacingMeters, contacts);
        const nextY = mode === "horizontal"
          ? box.position[1]
          : anchoredCenterForResize(box, "y", newSize[1], spacingMeters, contacts);

        const basePos: [number, number, number] = [nextX, Math.max(newSize[1] / 2, nextY), nextZ];
        const combined = [
          ...others,
          ...updated,
          ...selectedList.slice(i + 1),
        ];
        const sideAttached = isSideFaceAttached(basePos, newSize, combined, box.id);
        const supportY = computeSupportHeightAtPosition(basePos, newSize, combined, box.id);
        const finalY = sideAttached ? Math.max(newSize[1] / 2, basePos[1]) : supportY;
        updated.push({
          ...box,
          size: newSize,
          position: [basePos[0], finalY, basePos[2]],
        });
      }

      for (const candidate of updated) {
        const overlaps = findOverlapsAt(candidate.position, candidate.size, candidate.id, others);
        if (overlaps.length) {
          const conflictIds = new Set<number>([candidate.id, ...overlaps.map(o => o.id)]);
          triggerResizeBlocked("Cannot rotate selection: blocked by nearby block.", Array.from(conflictIds));
          triggerBlockShake(conflictIds);
          return prev;
        }
      }

      for (let i = 0; i < updated.length; i++) {
        for (let j = i + 1; j < updated.length; j++) {
          const a = updated[i]!;
          const b = updated[j]!;
          if (overlaps3D(a.position, a.size, b.position, b.size)) {
            const conflictIds = new Set<number>([a.id, b.id]);
            triggerResizeBlocked("Cannot rotate selection: selected blocks would intersect.", Array.from(conflictIds));
            triggerBlockShake(conflictIds);
            return prev;
          }
        }
      }

      const lookup = new Map<number, BoxItem>();
      updated.forEach(b => lookup.set(b.id, b));
      if (resizeFeedback) setResizeFeedback(null);
      rotated = true;
      return prev.map(b => lookup.get(b.id) ?? b);
    });

    if (rotated) {
      ids.forEach(id => physicsMapRef.current.delete(id));
    }

    return rotated;
  };

  const rotateSelectionHorizontal = (_direction: 1 | -1) => rotateSelection("horizontal");
  const rotateSelectionVertical = (_direction: 1 | -1) => rotateSelection("vertical");

  const getCurrentSelectionBoxes = () => {
    if (selectedIds.size > 0) {
      return boxes.filter(b => selectedIds.has(b.id));
    }
    if (selectedId != null) {
      return boxes.filter(b => b.id === selectedId);
    }
    return [];
  };

  const boxesToClipboardEntries = (selection: BoxItem[]): ClipboardEntry[] => selection.map((b) => ({
    position: [b.position[0], b.position[1], b.position[2]],
    size: [b.size[0], b.size[1], b.size[2]],
    color: b.color,
    groupId: b.groupId,
  }));

  const copySelectionToClipboard = () => {
    const selection = getCurrentSelectionBoxes();
    if (!selection.length) return;
    const payload = boxesToClipboardEntries(selection);
    clipboardRef.current = payload;

    if (typeof navigator !== "undefined" && navigator.clipboard && "writeText" in navigator.clipboard) {
      const text = JSON.stringify({ type: "space-calc/boxes", boxes: payload });
      navigator.clipboard.writeText(text).catch(() => undefined);
    }
  };

  const duplicateSelection = () => {
    const selection = getCurrentSelectionBoxes();
    if (!selection.length) return;
    const payload = boxesToClipboardEntries(selection);
    clipboardRef.current = payload;
    pasteClipboardEntries(payload);
  };

  const tryPasteFromClipboard = () => {
    if (clipboardRef.current && clipboardRef.current.length) {
      pasteClipboardEntries(clipboardRef.current);
      return;
    }
    if (typeof navigator === "undefined" || !navigator.clipboard || !("readText" in navigator.clipboard)) return;
    navigator.clipboard.readText()
      .then((text) => {
        try {
          const parsed = JSON.parse(text);
          if (parsed && parsed.type === "space-calc/boxes" && Array.isArray(parsed.boxes)) {
            const entries: ClipboardEntry[] = parsed.boxes
              .map((box: any) => {
                if (!box) return null;
                const pos = Array.isArray(box.position) ? box.position.slice(0, 3) : null;
                const size = Array.isArray(box.size) ? box.size.slice(0, 3) : null;
                const color = typeof box.color === "string" ? box.color : "#999999";
                if (!pos || pos.length !== 3 || !size || size.length !== 3) return null;
                return {
                  position: [Number(pos[0]) || 0, Number(pos[1]) || spacingMeters / 2, Number(pos[2]) || 0] as [number, number, number],
                  size: [
                    Math.max(spacingMeters, Number(size[0]) || spacingMeters),
                    Math.max(spacingMeters, Number(size[1]) || spacingMeters),
                    Math.max(spacingMeters, Number(size[2]) || spacingMeters),
                  ] as [number, number, number],
                  color,
                  groupId: (() => {
                    const numeric = typeof box.groupId === "number" ? box.groupId : Number(box.groupId);
                    if (Number.isFinite(numeric)) {
                      nextGroupIdRef.current = Math.max(nextGroupIdRef.current, (numeric as number) + 1);
                      return numeric as number;
                    }
                    return nextGroupIdRef.current++;
                  })(),
                };
              })
              .filter((entry: ClipboardEntry | null): entry is ClipboardEntry => entry != null);
            if (entries.length) {
              clipboardRef.current = entries;
              pasteClipboardEntries(entries);
            }
          }
        } catch (err) {
          return;
        }
      })
      .catch(() => undefined);
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (isEditableTarget(e.target)) return;

      const key = e.key.toLowerCase();
      const isMod = e.metaKey || e.ctrlKey;

      if (isMod && !e.shiftKey && key === "c") {
        copySelectionToClipboard();
        return;
      }

      if (isMod && !e.shiftKey && key === "v") {
        e.preventDefault();
        tryPasteFromClipboard();
        return;
      }

      if (e.key === "Insert" && e.ctrlKey && !e.shiftKey) {
        copySelectionToClipboard();
        return;
      }

      if (e.key === "Insert" && e.shiftKey && !e.ctrlKey) {
        e.preventDefault();
        tryPasteFromClipboard();
      }
    };

    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
    };
  }, [boxes, selectedId, selectedIds, spacingMeters]);

  // --- Dragging state ---
  type MultiDragState = {
    ids: Set<number>;
    anchorId: number;
    anchorStart: [number, number, number];
    applied: [number, number];
  };
  const [draggingId, setDraggingId] = useState<number | null>(null);
  const lastValidDragPosRef = useRef<[number, number, number] | null>(null);
  const dragModeRef = useRef<"xz" | "y">("xz");
  const dragHistoryRef = useRef<Array<{ time: number; point: [number, number, number] }>>([]);
  const multiDragStateRef = useRef<MultiDragState | null>(null);
  // Drag arming (cooldown) before moving the selected block
  const dragActivationDelayMs = 120;
  const dragPixelThreshold = 6; // px
  const pendingDragRef = useRef<{
    id: number;
    startClient: [number, number];
    startTime: number;
    selectionIds: Set<number>;
  } | null>(null);
  const pendingDragModeRef = useRef<"xz" | "y">("xz");

  const recordDragPoint = (point: [number, number, number]) => {
    const now = typeof performance !== "undefined" ? performance.now() : Date.now();
    dragHistoryRef.current.push({ time: now, point });
    while (dragHistoryRef.current.length > 6) dragHistoryRef.current.shift();
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (isEditableTarget(e.target)) return;
      if (draggingId != null) return;

      const step = spacingMeters;
      if (!(step > 0)) return;
      const key = e.key;
      const keyLower = key.length === 1 ? key.toLowerCase() : key.toLowerCase();
      const tempForward = new THREE.Vector3();
      const tempRight = new THREE.Vector3();

      const attemptCameraMove = (direction: "forward" | "backward" | "left" | "right") => {
        const controls = controlsRef.current as OrbitControlsImpl | null;
        const cameraObj = (controls?.object ?? null) as THREE.Object3D | null;
        if (!cameraObj) return false;
        if (typeof (cameraObj as any).updateMatrixWorld === "function") {
          (cameraObj as any).updateMatrixWorld();
        }
        const elements = cameraObj.matrixWorld?.elements;
        if (!elements) return false;

        tempForward.set(-elements[8], -elements[9], -elements[10]);
        tempForward.y = 0;
        if (tempForward.lengthSq() < 1e-6) {
          tempForward.set(0, 0, -1);
        } else {
          tempForward.normalize();
        }

        tempRight.set(elements[0], elements[1], elements[2]);
        tempRight.y = 0;
        if (tempRight.lengthSq() < 1e-6) {
          tempRight.set(1, 0, 0);
        } else {
          tempRight.normalize();
        }

        const basis = direction === "forward" || direction === "backward" ? tempForward : tempRight;
        const sign = direction === "forward" || direction === "right" ? 1 : -1;
        const dx = basis.x * step * sign;
        const dz = basis.z * step * sign;
        if (!Number.isFinite(dx) || !Number.isFinite(dz)) return false;
        const moved = moveSelectionBy(dx, dz);
        if (moved) {
          e.preventDefault();
        }
        return moved;
      };

      if (e.shiftKey && !e.metaKey && !e.ctrlKey && !e.altKey) {
        let handled = false;
        switch (key) {
          case "ArrowLeft":
            handled = rotateSelectionHorizontal(-1);
            break;
          case "ArrowRight":
            handled = rotateSelectionHorizontal(1);
            break;
          case "ArrowUp":
            handled = rotateSelectionVertical(1);
            break;
          case "ArrowDown":
            handled = rotateSelectionVertical(-1);
            break;
          default: {
            if (keyLower === "a") handled = rotateSelectionHorizontal(-1);
            else if (keyLower === "d") handled = rotateSelectionHorizontal(1);
            else if (keyLower === "w") handled = rotateSelectionVertical(1);
            else if (keyLower === "s") handled = rotateSelectionVertical(-1);
          }
        }
        if (handled) {
          e.preventDefault();
        }
        return;
      }

      if (e.metaKey || e.ctrlKey || e.altKey) return;

      switch (key) {
        case "ArrowUp":
          attemptCameraMove("forward");
          return;
        case "ArrowDown":
          attemptCameraMove("backward");
          return;
        case "ArrowLeft":
          attemptCameraMove("left");
          return;
        case "ArrowRight":
          attemptCameraMove("right");
          return;
        default: {
          if (keyLower === "w") attemptCameraMove("forward");
          else if (keyLower === "s") attemptCameraMove("backward");
          else if (keyLower === "a") attemptCameraMove("left");
          else if (keyLower === "d") attemptCameraMove("right");
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [draggingId, isEditableTarget, moveSelectionBy, rotateSelectionHorizontal, rotateSelectionVertical, spacingMeters]);

  // --- Physics state ---
  type PhysicsState = {
    velocity: [number, number, number];
  };
  const physicsMapRef = useRef<Map<number, PhysicsState>>(new Map());

  function PhysicsController() {
    useFrame((_, delta) => {
      const physicsMap = physicsMapRef.current;
      if (physicsMap.size === 0) return;

      const gravity = -30;
      const restitution = 0.25;
      const linearDamping = 2.5;
      const groundFriction = 0.7;
      const settleSpeed = 0.3;
      const settleSpeedSq = settleSpeed * settleSpeed;
      const settleHeightTolerance = Math.max(spacingMeters * 0.25, 0.02);

      setBoxes(prev => {
        const next = prev.map(b => ({
          ...b,
          position: [...b.position] as [number, number, number],
        }));
        const indexById = new Map<number, number>();
        next.forEach((b, idx) => indexById.set(b.id, idx));
        const removal: number[] = [];

        // Integrate velocities and positions
        for (const [id, state] of physicsMap.entries()) {
          const idx = indexById.get(id);
          if (idx == null || next[idx] == null) {
            removal.push(id);
            continue;
          }
          const box = next[idx]!;
          const vel = state.velocity;

          vel[1] += gravity * delta;

          const damping = Math.exp(-linearDamping * delta);
          vel[0] *= damping;
          vel[1] *= damping;
          vel[2] *= damping;

          box.position[0] += vel[0] * delta;
          box.position[1] += vel[1] * delta;
          box.position[2] += vel[2] * delta;
        }

        // Keep boxes above the ground plane
        for (const [id, state] of physicsMap.entries()) {
          const idx = indexById.get(id);
          if (idx == null || next[idx] == null) continue;
          const box = next[idx]!;
          const halfY = box.size[1] / 2;
          if (box.position[1] < halfY) {
            box.position[1] = halfY;
            if (state.velocity[1] < 0) {
              state.velocity[1] = -state.velocity[1] * restitution;
              state.velocity[0] *= groundFriction;
              state.velocity[2] *= groundFriction;
            }
            if (Math.abs(state.velocity[1]) < 0.2) state.velocity[1] = 0;
          }
        }

        // Resolve box-box collisions with multiple iterations
        for (let iter = 0; iter < 4; iter++) {
          for (let i = 0; i < next.length; i++) {
            for (let j = i + 1; j < next.length; j++) {
              const a = next[i];
              const b = next[j];
              if (!a || !b) continue;
              const dx = b.position[0] - a.position[0];
              const dy = b.position[1] - a.position[1];
              const dz = b.position[2] - a.position[2];
              const overlapX = a.size[0] / 2 + b.size[0] / 2 - Math.abs(dx);
              const overlapY = a.size[1] / 2 + b.size[1] / 2 - Math.abs(dy);
              const overlapZ = a.size[2] / 2 + b.size[2] / 2 - Math.abs(dz);
              if (overlapX <= 0 || overlapY <= 0 || overlapZ <= 0) continue;

              // Treat bodies without physics state as static/immovable.
              // Do NOT create new physics states here to avoid waking resting blocks.
              const sA = physicsMap.get(a.id) ?? null;
              const sB = physicsMap.get(b.id) ?? null;
              if (!sA && !sB) continue;

              const minOverlap = Math.min(overlapX, overlapY, overlapZ);
              let axisIndex = 0;
              let dir = Math.sign(dx || 1);
              if (minOverlap === overlapY) {
                axisIndex = 1;
                dir = Math.sign(dy || 1);
              } else if (minOverlap === overlapZ) {
                axisIndex = 2;
                dir = Math.sign(dz || 1);
              }
              if (dir === 0) dir = 1;

              const penetration = minOverlap + 1e-4;
              const correction = penetration;
              const movableA = sA != null;
              const movableB = sB != null;
              const shareA = movableA && movableB ? 0.5 : movableA ? 1 : 0;
              const shareB = movableA && movableB ? 0.5 : movableB ? 1 : 0;
              type Axis = 0 | 1 | 2;
              const axis = axisIndex as Axis;
              if (shareA) a.position[axis] -= dir * shareA * correction;
              if (shareB) b.position[axis] += dir * shareB * correction;

              const invMassA = movableA ? 1 : 0;
              const invMassB = movableB ? 1 : 0;
              const vAaxis = movableA ? (sA as PhysicsState).velocity[axis] : 0;
              const vBaxis = movableB ? (sB as PhysicsState).velocity[axis] : 0;
              const vn = (vBaxis - vAaxis) * dir;
              const massDenom = invMassA + invMassB;
              if (vn < 0 && massDenom > 0) {
                const j = -(1 + restitution) * vn / massDenom;
                if (movableA) (sA as PhysicsState).velocity[axis] -= j * invMassA * dir;
                if (movableB) (sB as PhysicsState).velocity[axis] += j * invMassB * dir;
              }

              const tangentIndices: Axis[] = axis === 0 ? [1, 2] : axis === 1 ? [0, 2] : [0, 1];
              const slideFriction = 0.9;
              for (const ti of tangentIndices) {
                if (movableA) (sA as PhysicsState).velocity[ti] *= slideFriction;
                if (movableB) (sB as PhysicsState).velocity[ti] *= slideFriction;
              }
            }
          }
        }

        // Snap resting bodies back to the grid
        for (const [id, state] of physicsMap.entries()) {
          const idx = indexById.get(id);
          if (idx == null || next[idx] == null) {
            removal.push(id);
            continue;
          }
          const box = next[idx]!;
          const speedSq = state.velocity[0] * state.velocity[0]
            + state.velocity[1] * state.velocity[1]
            + state.velocity[2] * state.velocity[2];
          const supportYRaw = computeSupportHeightFor(id, box.position, box.size, next);
          const closeToSupport = Math.abs(box.position[1] - supportYRaw) < settleHeightTolerance;
          const sideAttached = isSideFaceAttached(box.position, box.size, next.filter(o => o.id !== id), id);

          if ((closeToSupport || sideAttached) && speedSq < settleSpeedSq) {
            const halfY = box.size[1] / 2;
            // If side-attached, preserve current Y; otherwise, align to vertical support
            const alignedSupportY = sideAttached
              ? Math.max(halfY, box.position[1])
              : Math.max(halfY, snap(supportYRaw - halfY, spacingMeters) + halfY);
            const baseSnap: [number, number, number] = [
              snapCenterToEdges(box.position[0], box.size[0], spacingMeters),
              alignedSupportY,
              snapCenterToEdges(box.position[2], box.size[2], spacingMeters),
            ];
            const neighbors = next.filter(o => o.id !== id);
            // Prefer stronger magnetic snapping when resting on another block, weaker when resting on floor
            const restingOnBlock = sideAttached || (supportYRaw > halfY + 1e-6);
            const snapThreshold = restingOnBlock ? Math.min(spacingMeters * 0.85, spacingMeters) : Math.max(spacingMeters * 0.25, 0.01);
            let snapped = findAdjacentSnapPosition(
              baseSnap,
              box.size,
              neighbors,
              spacingMeters,
              snapThreshold,
              id,
            );
            const resolved = resolveNonIntersectingPosition(snapped, box.size, id, next);
            const finalPos = resolved ?? snapped;
            next[idx] = { ...box, position: finalPos } as BoxItem;
            state.velocity = [0, 0, 0];
            removal.push(id);
          }
        }

        removal.forEach(id => physicsMap.delete(id));
        return next;
      });
    });
    return null;
  }

  // Nested controller to handle global pointer events and raycasting during drag
  function DragController() {
    const { camera, gl } = useThree();
    useEffect(() => {
      if (draggingId == null) return;
      if (controlsRef.current) (controlsRef.current as any).enabled = false;
      const raycaster = new THREE.Raycaster();
      const groundPlane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0); // y=0
      const ndc = new THREE.Vector2();

      const onMove = (e: PointerEvent) => {
        const rect = gl.domElement.getBoundingClientRect();
        ndc.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
        ndc.y = -(((e.clientY - rect.top) / rect.height) * 2 - 1);
        raycaster.setFromCamera(ndc, camera as THREE.Camera);
        const id = draggingId!;
        const cur = boxes.find(b => b.id === id);
        if (!cur) return;
        const multiDragInfo = multiDragStateRef.current;
        const isMultiDrag = multiDragInfo != null && multiDragInfo.ids.size > 1;

        if (dragModeRef.current === "y") {
          if (isMultiDrag) return;
          const last = lastValidDragPosRef.current ?? cur.position;
          const camDir = new THREE.Vector3();
          (camera as THREE.Camera as any).getWorldDirection?.(camDir);
          const planeNormal = camDir.clone();
          const planePoint = new THREE.Vector3(last[0], 0, last[2]);
          const verticalPlane = new THREE.Plane().setFromNormalAndCoplanarPoint(planeNormal, planePoint);
          const hitPoint = new THREE.Vector3();
          if (!raycaster.ray.intersectPlane(verticalPlane, hitPoint)) return;
          let y = Math.max(cur.size[1] / 2, hitPoint.y);
          const snappedY = Math.max(cur.size[1] / 2, snap(y - cur.size[1] / 2, spacingMeters) + cur.size[1] / 2);
          const candidate: [number, number, number] = [last[0], snappedY, last[2]];
          recordDragPoint([candidate[0], snappedY, candidate[2]]);
          lastValidDragPosRef.current = candidate;
          setBoxes(prev => prev.map(b => (b.id === id ? { ...b, position: candidate } : b)));
        } else {
          const point = new THREE.Vector3();
          const hit = raycaster.ray.intersectPlane(groundPlane, point);
          if (!hit) return;
          const [w, h, d] = cur.size;
          if (isMultiDrag && multiDragInfo) {
            const snappedX = snapCenterToEdges(point.x, w, spacingMeters);
            const snappedZ = snapCenterToEdges(point.z, d, spacingMeters);
            const desiredMovement: [number, number] = [
              snappedX - multiDragInfo.anchorStart[0],
              snappedZ - multiDragInfo.anchorStart[2],
            ];
            const [appliedX, appliedZ] = multiDragInfo.applied;
            const deltaX = desiredMovement[0] - appliedX;
            const deltaZ = desiredMovement[1] - appliedZ;
            if (Math.abs(deltaX) >= 1e-6 || Math.abs(deltaZ) >= 1e-6) {
              const moved = moveSelectionByLatestRef.current(deltaX, deltaZ, multiDragInfo.ids);
              if (moved) {
                multiDragInfo.applied = [appliedX + deltaX, appliedZ + deltaZ];
              }
            }
            return;
          }
          const others = boxes.filter(b => b.id !== id);
          let candidate: [number, number, number] = [
            snapCenterToEdges(point.x, w, spacingMeters),
            cur.position[1],
            snapCenterToEdges(point.z, d, spacingMeters),
          ];
          candidate = findAdjacentSnapPosition(candidate, cur.size, others, spacingMeters);
          const supportYForCandidate = computeSupportHeightAtPosition(candidate, cur.size, others);
          const supportGain = supportYForCandidate - candidate[1];
          // If there is clear vertical support above our current drag height, prefer stacking over side snapping.
          const hoverThreshold = Math.max(h * 0.35, spacingMeters * 0.2);
          const sideCandidate = findSideAttachmentCandidate(candidate, cur.size, others, spacingMeters, id);
          if (sideCandidate && supportGain <= hoverThreshold) {
            candidate = sideCandidate;
          } else {
            candidate = [candidate[0], supportYForCandidate, candidate[2]];
          }
          const resolved = resolveNonIntersectingPosition(candidate, cur.size, id, others);
          let nextPos: [number, number, number];
          if (resolved) {
            nextPos = resolved;
          } else if (!anyOverlapAt(candidate, cur.size, id, others)) {
            nextPos = candidate;
          } else {
            nextPos = lastValidDragPosRef.current ?? cur.position;
          }
          const attachedSide = isSideFaceAttached(nextPos, cur.size, others, id);
          const stabilizedY = attachedSide
            ? Math.max(h / 2, nextPos[1])
            : computeSupportHeightAtPosition(nextPos, cur.size, others);
          const finalPos: [number, number, number] = [nextPos[0], stabilizedY, nextPos[2]];
          lastValidDragPosRef.current = finalPos;
          recordDragPoint([finalPos[0], finalPos[1], finalPos[2]]);
          setBoxes(prev => prev.map(b => (b.id === id ? { ...b, position: finalPos } : b)));
        }
      };

      const onUp = () => {
        window.removeEventListener("pointermove", onMove);
        window.removeEventListener("pointerup", onUp);
        if (controlsRef.current) (controlsRef.current as any).enabled = true;
        const id = draggingId!;
        const multiDragInfo = multiDragStateRef.current;
        multiDragStateRef.current = null;
        setDraggingId(null);

        if (multiDragInfo && multiDragInfo.ids.size > 1) {
          dragHistoryRef.current = [];
          lastValidDragPosRef.current = null;
          return;
        }

        const currentBox = boxes.find(b => b.id === id) || null;
        const fallbackY = currentBox ? currentBox.size[1] / 2 : spacingMeters / 2;
        const finalPos = (lastValidDragPosRef.current
          ? [...lastValidDragPosRef.current]
          : currentBox
            ? [...currentBox.position]
            : [0, fallbackY, 0]) as [number, number, number];

        // Commit final position immediately
        setBoxes(prev => prev.map(b => (b.id === id ? { ...b, position: finalPos } : b)));

        recordDragPoint([finalPos[0], finalPos[1], finalPos[2]]);
        const history = dragHistoryRef.current;
        // Build a projected list that includes the box at its final position for support checks
        const projectedList = boxes.map(b => (b.id === id ? { ...b, position: finalPos } : b));
        const supportYRaw = computeSupportHeightFor(
          id,
          finalPos,
          (currentBox ?? { size: [spacingMeters, spacingMeters, spacingMeters] }).size as [number, number, number],
          projectedList,
        );
        const settleHeightTolerance = Math.max(spacingMeters * 0.25, 0.02);
        const isRestingOnSupport = Math.abs(finalPos[1] - supportYRaw) < settleHeightTolerance;
        const isSideAttached = isSideFaceAttached(
          finalPos,
          (currentBox ?? { size: [spacingMeters, spacingMeters, spacingMeters] }).size as [number, number, number],
          projectedList.filter(o => o.id !== id),
          id,
        );
        const isSupported = isRestingOnSupport || isSideAttached;
        if (history.length === 0) {
          // No fling; if we're supported (floor or side), ensure physics is fully disabled
          dragHistoryRef.current = [];
          if (isSupported) {
            physicsMapRef.current.delete(id);
          } else {
            physicsMapRef.current.set(id, { velocity: [0, 0, 0] });
          }
          lastValidDragPosRef.current = null;
          return;
        }
        const lastEntry = history[history.length - 1]!;
        let firstEntry = lastEntry;
        for (let i = history.length - 2; i >= 0; i--) {
          const candidate = history[i];
          if (!candidate) continue;
          if (lastEntry.time - candidate.time > 150) break;
          firstEntry = candidate;
        }
        const dtMs = Math.max(lastEntry.time - firstEntry.time, 0);
        const dt = dtMs / 1000;
        const maxSpeed = 25;
        const clampComponent = (value: number) => Math.max(-maxSpeed, Math.min(maxSpeed, value));
        let velocity: [number, number, number] = [0, 0, 0];
        if (dt > 0) {
          if (dragModeRef.current === "y") {
            const vy = (lastEntry.point[1] - firstEntry.point[1]) / dt;
            velocity = [0, clampComponent(vy), 0];
          } else {
            const vx = (lastEntry.point[0] - firstEntry.point[0]) / dt;
            const vz = (lastEntry.point[2] - firstEntry.point[2]) / dt;
            velocity = [clampComponent(vx), 0, clampComponent(vz)];
          }
        }
        dragHistoryRef.current = [];
        // If the box is supported (ground or attached to another block), fully disable physics instead of adding it
        if (isSupported) {
          physicsMapRef.current.delete(id);
        } else {
          physicsMapRef.current.set(id, { velocity });
        }
        lastValidDragPosRef.current = null;
      };

      window.addEventListener("pointermove", onMove);
      window.addEventListener("pointerup", onUp);
      return () => {
        window.removeEventListener("pointermove", onMove);
        window.removeEventListener("pointerup", onUp);
        if (controlsRef.current) (controlsRef.current as any).enabled = true;
      };
    }, [draggingId, boxes, spacingMeters]);
    return null;
  }

  // Separate controller that listens for pointer movement to decide when to start dragging
  function DragArmingController() {
    useEffect(() => {
      const onMove = (e: PointerEvent) => {
        if (draggingId != null) return; // already dragging
        const pending = pendingDragRef.current;
        if (!pending) return;
        const dx = e.clientX - pending.startClient[0];
        const dy = e.clientY - pending.startClient[1];
        const distSq = dx * dx + dy * dy;
        const timeElapsed = (typeof performance !== "undefined" ? performance.now() : Date.now()) - pending.startTime;
        if (distSq >= dragPixelThreshold * dragPixelThreshold || timeElapsed >= dragActivationDelayMs) {
          // Activate drag
          const id = pending.id;
          const selectionIds = pending.selectionIds ?? new Set<number>([id]);
          pendingDragRef.current = null;
          dragModeRef.current = pendingDragModeRef.current;
          const currentBox = boxes.find(b => b.id === id) || null;
          if (!currentBox) return;
          lastValidDragPosRef.current = [...currentBox.position] as [number, number, number];
          physicsMapRef.current.delete(id);
          const selectionSnapshot = new Set(selectionIds);
          const isMultiDrag = selectionSnapshot.size > 1;
          multiDragStateRef.current = isMultiDrag
            ? {
              ids: selectionSnapshot,
              anchorId: id,
              anchorStart: [...currentBox.position] as [number, number, number],
              applied: [0, 0],
            }
            : null;
          dragHistoryRef.current = [];
          if (!isMultiDrag) {
            recordDragPoint([currentBox.position[0], currentBox.position[1], currentBox.position[2]]);
          }
          setDraggingId(id);
        }
      };
      const onUp = () => {
        // If mouse released before drag activation, cancel pending drag
        pendingDragRef.current = null;
      };
      window.addEventListener("pointermove", onMove);
      window.addEventListener("pointerup", onUp);
      return () => {
        window.removeEventListener("pointermove", onMove);
        window.removeEventListener("pointerup", onUp);
      };
    }, [draggingId, boxes]);
    return null;
  }

  function BoxInstance({
    box,
    isSelected,
    onPointerDown,
    onClick,
    onDoubleClick,
    isDarkTheme,
  }: {
    box: BoxItem;
    isSelected: boolean;
    onPointerDown: (e: ThreeEvent<PointerEvent>) => void;
    onClick: (e: ThreeEvent<MouseEvent>) => void;
    onDoubleClick: (e: ThreeEvent<MouseEvent>) => void;
    isDarkTheme: boolean;
  }) {
    const [shakeOffset, setShakeOffset] = useState<[number, number, number]>([0, 0, 0]);

    // Animate a quick vibration whenever this block is flagged to shake.
    useFrame(() => {
      const entry = shakingIdsRef.current.get(box.id);
      const now = typeof performance !== "undefined" ? performance.now() : Date.now();
      if (!entry || now >= entry.expires) {
        if (entry && now >= entry.expires) shakingIdsRef.current.delete(box.id);
        setShakeOffset(prev => {
          if (Math.abs(prev[0]) > 1e-4 || Math.abs(prev[1]) > 1e-4 || Math.abs(prev[2]) > 1e-4) {
            return [0, 0, 0] as [number, number, number];
          }
          return prev;
        });
        return;
      }

      const duration = Math.max(entry.expires - entry.started, 1);
      const elapsed = now - entry.started;
      const clamped = Math.min(Math.max(elapsed / duration, 0), 1);
      const decay = 1 - clamped;
      const intensity = Math.min(spacingMeters * 0.18, 0.9) * decay;
      const timeFactor = now * 0.02;
      const nextOffset: [number, number, number] = [
        Math.sin(timeFactor * 1.9 + box.id * 0.37) * intensity,
        Math.sin(timeFactor * 2.6 + box.id * 0.51) * intensity * 0.5,
        Math.cos(timeFactor * 2.1 + box.id * 0.63) * intensity,
      ];
      setShakeOffset(prev => {
        if (
          Math.abs(nextOffset[0] - prev[0]) < 1e-4
          && Math.abs(nextOffset[1] - prev[1]) < 1e-4
          && Math.abs(nextOffset[2] - prev[2]) < 1e-4
        ) {
          return prev;
        }
        return nextOffset;
      });
    });

    const finalPosition = useMemo<[number, number, number]>(() => [
      box.position[0] + shakeOffset[0],
      box.position[1] + shakeOffset[1],
      box.position[2] + shakeOffset[2],
    ], [
      box.position[0],
      box.position[1],
      box.position[2],
      shakeOffset[0],
      shakeOffset[1],
      shakeOffset[2],
    ]);

    return (
      <mesh
        userData={{ isBox: true }}
        position={finalPosition}
        onPointerDown={onPointerDown}
        onClick={onClick}
        onDoubleClick={onDoubleClick}
      >
        <boxGeometry args={box.size} />
        <meshStandardMaterial color={box.color} transparent opacity={0.95} />
        {isSelected && (
          <Edges visible scale={1.01} threshold={15} color={isDarkTheme ? "#f5f5f5" : "#111827"} />
        )}
      </mesh>
    );
  }

  // Save the initial camera/controls state for reliable resets
  useEffect(() => {
    controlsRef.current?.saveState();
  }, []);

  // When spacing changes, reset the view so navigation doesn't "break"
  useEffect(() => {
    controlsRef.current?.reset();
  }, [spacingMeters]);

  useEffect(() => {
    if (!showHelp) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setShowHelp(false);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [showHelp]);

  return (
    <div className="relative w-screen h-screen">
      <Canvas
        className="absolute inset-0"
        camera={{ position: [12, 10, 12], near: 0.1, far: 1000, fov: 60 }}
        gl={{ antialias: true, alpha: true }}
        onPointerMissed={(e) => {
          // Clicks that miss objects will clear selection
          if (e.button === 0) {
            setSelectedId(null);
            setSelectedIds(new Set());
          }
        }}
      >
        <UpdateCameraFar far={Math.max(1000, gridFadeDistance * 2)} />
        {/* Keep background transparent so site background shows through */}
        <ambientLight intensity={0.6} />
        <directionalLight position={[8, 12, 5]} intensity={0.6} />
        {/* Subtle ground reference grid that adapts to theme */}
        <DreiGrid
          infiniteGrid
          fadeDistance={gridFadeDistance}
          cellSize={spacingMeters}
          sectionSize={spacingMeters * 5}
          cellThickness={isDark ? 0.5 : 0.4}
          sectionThickness={isDark ? 1 : 0.8}
          cellColor={isDark ? "#475569" : "#cbd5e1"}
          sectionColor={isDark ? "#94a3b8" : "#64748b"}
          position={[0, -0.001, 0]}
        />
        {/* Mirror grid to be visible from below */}
        <DreiGrid
          infiniteGrid
          fadeDistance={gridFadeDistance}
          cellSize={spacingMeters}
          sectionSize={spacingMeters * 5}
          cellThickness={isDark ? 0.5 : 0.4}
          sectionThickness={isDark ? 1 : 0.8}
          cellColor={isDark ? "#475569" : "#cbd5e1"}
          sectionColor={isDark ? "#94a3b8" : "#64748b"}
          position={[0, 0.001, 0]}
          rotation={[Math.PI, 0, 0]}
        />
        {/* Hide vertex points for a cleaner view; keep ground grid for reference */}
        {/* <GridPoints spacingMeters={spacingMeters} isDark={isDark} /> */}
        {markModeEnabled && markEvaluation.showItems.map((item, idx) => (
          <Text
            key={item.id}
            position={[
              -gridFadeDistance * 0.4,
              Math.max(spacingMeters * 0.2, 0.05),
              -gridFadeDistance * 0.25 - idx * spacingMeters * 2,
            ]}
            fontSize={Math.max(spacingMeters * 0.35, 0.6)}
            color={isDark ? "#cdd4ff" : "#0f172a"}
            anchorX="left"
            anchorY="middle"
            outlineWidth={0.004}
            outlineColor={isDark ? "#0b1021" : "#ffffff"}
            renderOrder={2}
            material-depthTest={false}
            material-transparent
          >
            {item.label}
          </Text>
        ))}
        {/* Interactive ground plane for placing boxes */}
        <mesh
          position={[0, 0, 0]}
          rotation={[-Math.PI / 2, 0, 0]}
          onDoubleClick={(e) => {
            e.stopPropagation();
            const p = e.point;
            addBoxAt(p.x, p.z);
          }}
          onPointerDown={(e) => {
            if (e.button === 0) {
              setSelectedId(null);
              setSelectedIds(new Set());
            }
          }}
        >
          <planeGeometry args={[gridFadeDistance * 2, gridFadeDistance * 2]} />
          <meshBasicMaterial visible={false} side={THREE.DoubleSide} />
        </mesh>

        {/* Boxes */}
        {boxes.map((b) => (
          <BoxInstance
            key={b.id}
            box={b}
            isSelected={selectedId === b.id || selectedIds.has(b.id)}
            isDarkTheme={isDark}
            onPointerDown={(e) => {
              e.stopPropagation();
              const selection = selectBox(b.id, e.metaKey || e.ctrlKey);
              if (!selection.has(b.id)) {
                pendingDragRef.current = null;
                return;
              }
              pendingDragRef.current = {
                id: b.id,
                startClient: [e.clientX, e.clientY],
                startTime: (typeof performance !== "undefined" ? performance.now() : Date.now()),
                selectionIds: selection,
              };
              pendingDragModeRef.current = e.shiftKey ? "y" : "xz";
            }}
            onClick={(e) => {
              e.stopPropagation();
            }}
            onDoubleClick={(e) => {
              e.stopPropagation();
              const face = (e as any).face as THREE.Face | undefined;
              const obj = e.object as THREE.Mesh;
              if (!face || !obj) return;
              const normal = face.normal.clone();
              const normalMatrix = new THREE.Matrix3().getNormalMatrix(obj.matrixWorld);
              normal.applyNormalMatrix(normalMatrix).normalize();

              const ax = Math.abs(normal.x);
              const ay = Math.abs(normal.y);
              const az = Math.abs(normal.z);
              let axis: "x" | "y" | "z" = "x";
              if (ay >= ax && ay >= az) axis = "y"; else if (az >= ax && az >= ay) axis = "z"; else axis = "x";
              const sign = axis === "x" ? Math.sign(normal.x) : axis === "y" ? Math.sign(normal.y) : Math.sign(normal.z);

              const [ow, oh, od] = b.size;
              const nhalf = spacingMeters / 2;
              const pos: [number, number, number] = [...b.position] as [number, number, number];
              if (axis === "x") {
                pos[0] = snapCenterToEdges(b.position[0] + sign * (ow / 2 + nhalf), spacingMeters, spacingMeters);
                pos[1] = b.position[1];
              } else if (axis === "y") {
                const cy = b.position[1] + sign * (oh / 2 + nhalf);
                pos[1] = Math.max(nhalf, cy);
              } else {
                pos[2] = snapCenterToEdges(b.position[2] + sign * (od / 2 + nhalf), spacingMeters, spacingMeters);
                pos[1] = b.position[1];
              }
              pos[0] = axis === "x" ? pos[0] : snapCenterToEdges(pos[0], spacingMeters, spacingMeters);
              pos[2] = axis === "z" ? pos[2] : snapCenterToEdges(pos[2], spacingMeters, spacingMeters);
              pos[1] = Math.max(nhalf, pos[1]);

              const candidateSize: [number, number, number] = [spacingMeters, spacingMeters, spacingMeters];
              const resolved = resolveNonIntersectingPosition(pos, candidateSize);
              const finalPos = resolved ?? pos;
              const id = nextIdRef.current++;
              const groupId = nextGroupIdRef.current++;
              const idx = ((id - 1) % palette.length + palette.length) % palette.length;
              const color = palette[idx] ?? "#999999";
              setBoxes(prev => [...prev, { id, position: finalPos, size: [spacingMeters, spacingMeters, spacingMeters], color, groupId }]);
              setSelectedId(id);
              setSelectedIds(new Set([id]));
            }}
          />
        ))}

        {/* Mark Mode Objects - 3D boxes from the markdown editor */}
        {markModeEnabled && markEvaluation.objects.map((obj) => (
          <group key={obj.id}>
            <mesh position={obj.position}>
              <boxGeometry args={obj.size} />
              <meshStandardMaterial color={obj.color} transparent opacity={0.85} />
              <Edges visible scale={1.01} threshold={15} color={isDark ? "#ffffff" : "#000000"} lineWidth={1} />
            </mesh>
            {obj.label && (
              <Text
                position={[
                  obj.position[0],
                  obj.position[1] + obj.size[1] / 2 + 0.3,
                  obj.position[2],
                ]}
                fontSize={Math.max(spacingMeters * 0.4, 0.4)}
                color={isDark ? "#ffffff" : "#000000"}
                anchorX="center"
                anchorY="bottom"
                outlineWidth={0.02}
                outlineColor={obj.color}
              >
                {obj.label}
              </Text>
            )}
          </group>
        ))}

        {/* Mark Mode Labels - text attached to nearest objects */}
        {markModeEnabled && markEvaluation.labels.map((label, idx) => {
          // Find the object this label is attached to
          const attachedObj = label.attachToObjectId 
            ? markEvaluation.objects.find(o => o.id === label.attachToObjectId)
            : null;
          
          // Calculate label position
          let labelPos: [number, number, number];
          if (attachedObj) {
            // Position below and slightly in front of the attached object
            const labelIndex = markEvaluation.labels
              .filter(l => l.attachToObjectId === label.attachToObjectId)
              .indexOf(label);
            labelPos = [
              attachedObj.position[0],
              attachedObj.position[1] - attachedObj.size[1] / 2 - 0.2 - labelIndex * 0.5,
              attachedObj.position[2] + attachedObj.size[2] / 2 + 0.3,
            ];
          } else {
            // No attached object, position on the ground
            labelPos = [
              -gridFadeDistance * 0.3,
              0.1,
              -gridFadeDistance * 0.2 - idx * 1,
            ];
          }
          
          return (
            <Text
              key={label.id}
              position={labelPos}
              fontSize={Math.max(spacingMeters * 0.3, 0.35)}
              color={isDark ? "#fcd34d" : "#d97706"}
              anchorX="center"
              anchorY="top"
              outlineWidth={0.015}
              outlineColor={isDark ? "#1f2937" : "#ffffff"}
              renderOrder={3}
              material-depthTest={false}
            >
              {label.text}
            </Text>
          );
        })}

        <OrbitControls
          ref={controlsRef}
          enableDamping
          dampingFactor={0.1}
          target={[0, 0, 0]}
        />
        <DragController />
        <DragArmingController />
        <PhysicsController />
      </Canvas>

      {/* Top controls: Spacing, Units, Theme */}
      <div className="absolute top-4 left-4 right-4 z-10 flex items-start justify-between gap-4 pointer-events-none">
        <div className="flex flex-col gap-3 pointer-events-auto w-full max-w-sm">
          <Card className="bg-background/70 backdrop-blur border-muted shadow-md pointer-events-auto">
            <CardContent className="p-3">
              <div className="flex w-full items-center gap-2">
                <Select value={sceneState.activeId} onValueChange={handleSceneMenuSelect}>
                  <SelectTrigger id="scene-select" className="w-full">
                    <SelectValue placeholder="Choose a scene" />
                  </SelectTrigger>
                  <SelectContent>
                    {sceneState.scenes.map(scene => (
                      <SelectItem key={scene.id} value={scene.id}>
                        {scene.name}
                      </SelectItem>
                    ))}
                    <SelectSeparator />
                    <SelectItem value={SCENE_ACTION_CREATE}>+ New scene</SelectItem>
                  </SelectContent>
                </Select>
                <Button
                  ref={sceneMenuButtonRef}
                  variant="outline"
                  size="icon"
                  className="h-10 w-10 shrink-0"
                  aria-label="Edit scene"
                  onClick={() => setIsSceneMenuOpen(prev => !prev)}
                >
                  <MoreVertical className="size-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-background/70 backdrop-blur border-muted shadow-md pointer-events-auto">
            <CardContent className="p-3">
              {formattedMetrics ? (
                <div className="space-y-3 text-sm">
                  <div className="space-y-1">
                    <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Totals</div>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                      <span className="text-muted-foreground">Blocks</span>
                      <span className="text-right font-medium">{formattedMetrics.totals.count}</span>
                      <span className="text-muted-foreground">Volume</span>
                      <span className="text-right font-semibold">{formattedMetrics.totals.volumeDisplay}</span>
                      <span className="text-muted-foreground">Floorspace</span>
                      <span className="text-right font-semibold">{formattedMetrics.totals.areaDisplay}</span>
                    </div>
                  </div>
                  {formattedMetrics.groups.length > 0 && (
                    <div className="space-y-1.5">
                      <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">By group</div>
                      <div className="space-y-1.5">
                        {formattedMetrics.groups.map(group => (
                          <div
                            key={group.groupId}
                            className="flex items-center justify-between gap-3 rounded-md border border-muted/60 bg-background/40 px-2 py-1.5"
                          >
                            <div className="flex items-center gap-2">
                              <span
                                className="inline-flex h-2.5 w-2.5 rounded-full"
                                style={{ backgroundColor: group.color }}
                              />
                              <span className="text-xs font-medium leading-none">#{group.groupId}</span>
                              <span className="text-xs text-muted-foreground leading-none">({group.count})</span>
                            </div>
                            <div className="text-right text-xs leading-4">
                              <div className="font-medium">{group.volumeDisplay}</div>
                              <div className="text-muted-foreground">Floor {group.areaDisplay}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">Double-click to add a block to see total dimensions.</p>
              )}
            </CardContent>
          </Card>
        </div>
        <div className="flex flex-col items-end gap-2 pointer-events-auto">
          <div className="flex items-center gap-2">
            <Button
              variant={markModeEnabled ? "default" : "outline"}
              size="sm"
              onClick={toggleMarkMode}
              className="rounded-full px-3"
            >
              {markModeEnabled ? "Mark mode on" : "Mark mode"}
            </Button>
            <ThemeToggle className="pointer-events-auto" />
            <Button
              variant="outline"
              size="icon"
              aria-label="Show help"
              className="h-10 w-10 rounded-full"
              onClick={() => setShowHelp(true)}
            >
              ?
            </Button>
          </div>
        </div>
      </div>

      {markModeEnabled && (
        <MarkPadContainer
          lines={markLines}
          evaluation={markEvaluation}
          onLinesChange={handleMarkLinesChange}
          onResetTemplate={handleResetMarkTemplate}
        />
      )}

      {/* Bottom-left selection UI + feedback */}
      <div className="absolute bottom-4 left-4 z-10 flex flex-col gap-3 pointer-events-none">
        {resizeFeedback && (
          <div className="pointer-events-auto">
            <div
              role="alert"
              className="rounded-lg border border-destructive/40 bg-destructive/15 px-4 py-2 text-sm font-medium text-destructive shadow-lg backdrop-blur"
            >
              {resizeFeedback.message}
            </div>
          </div>
        )}

        {selectedBox && (
          <Card className="bg-background/70 backdrop-blur border-muted shadow-md pointer-events-auto">
            <CardContent className="p-3">
              <div className="grid gap-3 grid-cols-2 items-end">
                <div className="grid gap-1">
                  <Label htmlFor="box-w">Width</Label>
                  <Input
                    id="box-w"
                    ref={widthInputRef}
                    type="number"
                    step={dimensionStep}
                    min={dimensionStep}
                    value={roundTo(fromMeters(selectedBox.size[0], unit), spacingPrecision)}
                    onChange={(e) => {
                      const rawUnit = parseFloat(e.target.value);
                      const baseUnit = Number.isFinite(rawUnit) ? rawUnit : dimensionStep;
                      const clampedUnit = Math.max(dimensionStep, baseUnit);
                      const snappedUnit = Math.max(dimensionStep, Math.round(clampedUnit / dimensionStep) * dimensionStep);
                      const snappedMeters = roundTo(toMeters(snappedUnit, unit), 6);
                      const wBase = Math.max(spacingMeters, snap(snappedMeters, spacingMeters));
                      const wAligned = Math.max(spacingMeters, Math.round(wBase / spacingMeters) * spacingMeters);
                      const groupId = selectedBox.groupId;
                      updateGroupBoxes(groupId, (box, ctx) => ({
                        ...box,
                        size: [wAligned, box.size[1], box.size[2]] as [number, number, number],
                        position: [
                          anchoredCenterForResize(box, "x", wAligned, ctx.spacing, ctx.contacts),
                          box.position[1],
                          box.position[2],
                        ] as [number, number, number],
                      }), { context: "width" });
                    }}
                    className="w-28"
                  />
                </div>
                <div className="grid gap-1">
                  <Label htmlFor="box-h">Height</Label>
                  <Input
                    id="box-h"
                    type="number"
                    step={dimensionStep}
                    min={dimensionStep}
                    value={roundTo(fromMeters(selectedBox.size[1], unit), spacingPrecision)}
                    onChange={(e) => {
                      const rawUnit = parseFloat(e.target.value);
                      const baseUnit = Number.isFinite(rawUnit) ? rawUnit : dimensionStep;
                      const clampedUnit = Math.max(dimensionStep, baseUnit);
                      const snappedUnit = Math.max(dimensionStep, Math.round(clampedUnit / dimensionStep) * dimensionStep);
                      const snappedMeters = roundTo(toMeters(snappedUnit, unit), 6);
                      const hBase = Math.max(spacingMeters, snap(snappedMeters, spacingMeters));
                      const hAligned = Math.max(spacingMeters, Math.round(hBase / spacingMeters) * spacingMeters);
                      const groupId = selectedBox.groupId;
                      updateGroupBoxes(groupId, (box, ctx) => {
                        const nextY = anchoredCenterForResize(box, "y", hAligned, ctx.spacing, ctx.contacts);
                        return {
                          ...box,
                          size: [box.size[0], hAligned, box.size[2]] as [number, number, number],
                          position: [box.position[0], nextY, box.position[2]] as [number, number, number],
                        };
                      }, { context: "height" });
                    }}
                    className="w-28"
                  />
                </div>
                <div className="grid gap-1">
                  <Label htmlFor="box-d">Depth</Label>
                  <Input
                    id="box-d"
                    type="number"
                    step={dimensionStep}
                    min={dimensionStep}
                    value={roundTo(fromMeters(selectedBox.size[2], unit), spacingPrecision)}
                    onChange={(e) => {
                      const rawUnit = parseFloat(e.target.value);
                      const baseUnit = Number.isFinite(rawUnit) ? rawUnit : dimensionStep;
                      const clampedUnit = Math.max(dimensionStep, baseUnit);
                      const snappedUnit = Math.max(dimensionStep, Math.round(clampedUnit / dimensionStep) * dimensionStep);
                      const snappedMeters = roundTo(toMeters(snappedUnit, unit), 6);
                      const dBase = Math.max(spacingMeters, snap(snappedMeters, spacingMeters));
                      const dAligned = Math.max(spacingMeters, Math.round(dBase / spacingMeters) * spacingMeters);
                      const groupId = selectedBox.groupId;
                      updateGroupBoxes(groupId, (box, ctx) => ({
                        ...box,
                        size: [box.size[0], box.size[1], dAligned] as [number, number, number],
                        position: [
                          box.position[0],
                          box.position[1],
                          anchoredCenterForResize(box, "z", dAligned, ctx.spacing, ctx.contacts),
                        ] as [number, number, number],
                      }), { context: "depth" });
                    }}
                    className="w-28"
                  />
                </div>
                <div className="grid gap-1">
                  <Label htmlFor="box-color">Color</Label>
                  <Input
                    id="box-color"
                    type="text"
                    value={selectedBox.color}
                    onChange={(e) => {
                      const val = e.target.value.trim();
                      const groupId = selectedBox.groupId;
                      updateGroupBoxes(groupId, (box) => ({ ...box, color: val }), { skipCollision: true });
                    }}
                    className="w-28"
                  />
                </div>
              </div>
              <div className="mt-3 flex justify-end gap-2">
                <Button variant="outline" size="sm" onClick={duplicateSelection}>Duplicate</Button>
                <Button variant="destructive" size="sm" onClick={deleteSelected}>Delete</Button>
              </div>
            </CardContent>
          </Card>
        )}

        {selectionSummary && (
          <Card className="bg-background/70 backdrop-blur border-muted shadow-md pointer-events-auto">
            <CardContent className="p-3 space-y-3">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-sm font-semibold">Selection</p>
                  <p className="text-xs text-muted-foreground">
                    {selectionSummary.count} blocks · {selectionSummary.groupCount} group{selectionSummary.groupCount === 1 ? "" : "s"}
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={clearSelection}>Clear</Button>
                  <Button variant="outline" size="sm" onClick={duplicateSelection}>Duplicate</Button>
                  <Button variant="destructive" size="sm" onClick={deleteSelected}>Delete</Button>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 text-[0.75rem]">
                <div>
                  <div className="text-[0.6rem] uppercase tracking-wide text-muted-foreground">Volume</div>
                  <div className="font-medium text-sm text-foreground">{selectionSummary.volumeDisplay}</div>
                </div>
                <div>
                  <div className="text-[0.6rem] uppercase tracking-wide text-muted-foreground">Floorspace</div>
                  <div className="font-medium text-sm text-foreground">{selectionSummary.areaDisplay}</div>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2 text-[0.65rem] text-muted-foreground">
                <div>
                  <div className="uppercase tracking-wide">Width</div>
                  <div className="font-medium text-sm text-foreground">{selectionSummary.widthDisplay}</div>
                </div>
                <div>
                  <div className="uppercase tracking-wide">Depth</div>
                  <div className="font-medium text-sm text-foreground">{selectionSummary.depthDisplay}</div>
                </div>
                <div>
                  <div className="uppercase tracking-wide">Height</div>
                  <div className="font-medium text-sm text-foreground">{selectionSummary.heightDisplay}</div>
                </div>
              </div>
              <p className="text-[0.65rem] text-muted-foreground">
                Hold ⌘/Ctrl while clicking to toggle boxes.
              </p>
            </CardContent>
          </Card>
        )}
      </div>

      {isSceneMenuOpen && (
        <div className="absolute inset-0 z-40 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-background/70 backdrop-blur-sm"
            aria-hidden="true"
            onClick={() => closeSceneMenu()}
          />
          <div
            ref={sceneMenuRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="scene-menu-title"
            className="relative z-10 w-full max-w-md rounded-2xl border border-muted bg-popover p-6 shadow-2xl"
          >
            <button
              type="button"
              aria-label="Close edit scene"
              className="absolute right-4 top-4 cursor-pointer text-muted-foreground transition hover:text-foreground"
              onClick={() => closeSceneMenu()}
            >
              ×
            </button>
            <div className="pr-8">
              <h2 id="scene-menu-title" className="text-lg font-semibold">Edit scene</h2>
              <p className="text-sm text-muted-foreground">Apply changes to "{activeScene?.name ?? "Current scene"}".</p>
            </div>
            <div className="mt-4 space-y-4 text-sm">
              <div className="grid gap-1">
                <Label htmlFor="scene-name">Scene name</Label>
                <Input
                  id="scene-name"
                  value={sceneNameDraft}
                  onChange={(e) => setSceneNameDraft(e.target.value)}
                />
              </div>
              <div className="grid gap-1">
                <Label>Units</Label>
                <Select value={sceneUnitDraft} onValueChange={(v) => handleSceneUnitDraftChange(v as Unit)}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Units" />
                  </SelectTrigger>
                  <SelectContent data-scene-menu-portal="true">
                    <SelectItem value="m">meters</SelectItem>
                    <SelectItem value="ft">feet</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-1">
                <Label htmlFor="scene-spacing">Spacing</Label>
                <Input
                  id="scene-spacing"
                  type="number"
                  inputMode="decimal"
                  min={menuSpacingMin}
                  step={menuSpacingStep}
                  value={sceneSpacingDraft}
                  onChange={(e) => handleSpacingInputChange(e.target.value)}
                  onBlur={() => normalizeSceneSpacingDraft()}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      normalizeSceneSpacingDraft();
                    }
                    if (e.key === "Escape") {
                      e.preventDefault();
                      revertSpacingDraftToSaved();
                      (e.currentTarget as HTMLInputElement).blur();
                    }
                  }}
                />
              </div>
              <div className="flex items-center justify-between gap-3 pt-6">
                <Button
                  variant="destructive"
                  onClick={() => {
                    closeSceneMenu();
                    handleDeleteScene();
                  }}
                >
                  Delete scene
                </Button>
                <Button
                  onClick={handleSceneMenuSave}
                  disabled={sceneMenuSaveDisabled}
                >
                  Save changes
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

  {/* Theme toggle moved to top bar */}
      {showHelp && (
        <div className="absolute inset-0 z-30 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-background/80 backdrop-blur-sm"
            onClick={() => setShowHelp(false)}
          />
          <div className="relative z-10 w-full max-w-2xl mx-4 max-h-[80vh] overflow-y-auto rounded-xl border border-muted bg-background/95 p-6 shadow-xl">
            <div className="flex items-start justify-between gap-4 mb-4">
              <div>
                <h2 className="text-lg font-semibold">How to use Space Calc</h2>
                <p className="text-sm text-muted-foreground">Quick reference for building on the grid.</p>
              </div>
                <Button variant="ghost" size="sm" onClick={() => setShowHelp(false)}>
                  ×
                </Button>
            </div>
            <div className="space-y-4 text-sm leading-relaxed">
              <section>
                <h3 className="font-medium">Placing blocks</h3>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Double-click the ground grid to spawn a block snapped to the current spacing.</li>
                  <li>Double-click a block face to attach a new block directly to that face.</li>
                  <li>Blocks auto-snap to the grid and settle onto nearby supports.</li>
                </ul>
              </section>
              <section>
                <h3 className="font-medium">Selecting</h3>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Click a block to select it.</li>
                  <li>Hold ⌘/Ctrl while clicking additional blocks to add or remove them from the selection.</li>
                  <li>A secondary selection summary appears in the bottom-left when more than one block is picked, showing quick stats plus duplicate/delete actions.</li>
                  <li>The inspector panel shows details for the current selection.</li>
                </ul>
              </section>
              <section>
                <h3 className="font-medium">Moving &amp; rotating</h3>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Drag a selection to slide it across the grid. Hold Shift while dragging to move vertically.</li>
                  <li>Use Arrow keys or WASD to nudge the selection one grid step.</li>
                  <li>Hold Shift + Arrow keys (or Shift + WASD) to rotate: left/right for horizontal, up/down for vertical.</li>
                </ul>
              </section>
              <section>
                <h3 className="font-medium">Copy, paste &amp; delete</h3>
                <ul className="list-disc pl-5 space-y-1">
                  <li>⌘/Ctrl + C copies the current selection. ⌘/Ctrl + V pastes it nearby.</li>
                  <li>Delete or Backspace removes the selected blocks.</li>
                </ul>
              </section>
              <section>
                <h3 className="font-medium">Camera basics</h3>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Drag with the left mouse to orbit, right mouse (or Ctrl+drag) to pan, and scroll to zoom.</li>
                  <li>Use the spacing and unit controls in the top-left to change grid density.</li>
                </ul>
              </section>
            </div>
            <div className="mt-6 flex justify-end">
              <Button onClick={() => setShowHelp(false)}>Got it</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default GridScene;
